// ============================================================
// fmea.js
// EV Powertrain Simulator — core 모듈
// 기능안전 — FMEA, SOTIF, SME, DFMEA/PFMEA, FTA, HARA, FSR/TSR
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================

import { FAULT_MODES, FM_COMPONENT_MAP, FM_DETECTION_D, ASIL_S_TO_FMEA_SEV } from '../../constants/FAULT_MODES.js';
import { FTA_TOP_EVENTS, FTA_INTERMEDIATE_EVENTS, HOUSE_EVENTS } from '../../constants/FTA_EVENTS.js';

// ══════════════════════════════════════════════════════════════════
// [Fix-DC-FMEDA-1] calcDC_FMEDA — FMEDA 입력 기반 DC 산출
// ISO 26262-5 §8.4.5: DC = λ_DD / (λ_DD + λ_DU)
//   dd_fit: 검출된 위험 고장률 (Detected Dangerous), 단위 FIT
//   du_fit: 미검출 위험 고장률 (Undetected Dangerous), 단위 FIT
// ─ 컴포넌트에 fmeda 서브객체 + 유효한 dd/du 값이 있을 때만 계산
// ─ 없으면 comp.coverage 폴백 (하위 호환)
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// [Sprint-3] parseFMEDASupplierInput — FMEDA 공급업체 입력 워크플로우
// ISO 26262-5 §8.4 / AEC-Q100 기반 공급업체 데이터 정규화
//
// 입력 형식 (supplierData):
//   {
//     component_id: "IC-U1",     // 부품 식별자
//     lambda_total: 150,          // 전체 고장률 [FIT = 10⁻⁹/h]
//     dd_fit:  80,                // 진단 검출 가능 위험 고장 [FIT]
//     du_fit:  40,                // 진단 검출 불가 위험 고장 [FIT]
//     dp_fit:  15,                // 진단 검출 가능 안전 고장 [FIT]
//     np_fit:  15,                // 진단 무관 안전 고장 [FIT]
//     spf_immune: false,          // SPF 면제 여부 (ISO 26262-5 §8.4.5)
//     dc_source: "Supplier_v2",   // 출처 레이블 (감사 추적)
//     validated_by: "TUV_2024",   // 제3자 검증 기관
//   }
// 반환: comp.fmeda 구조와 호환되는 정규화 객체
//
// 유효성 검사:
//   ① lambda_total ≥ dd_fit + du_fit + dp_fit + np_fit (합계 일관성)
//   ② dd_fit, du_fit ≥ 0 (음수 불가)
//   ③ DC = dd_fit / (dd_fit + du_fit) ∈ [0, 1]
// ══════════════════════════════════════════════════════════════════
export function parseFMEDASupplierInput(supplierData) {
  if (!supplierData || typeof supplierData !== 'object') {
    return { ok: false, error: 'supplierData must be a non-null object' };
  }

  const {
    component_id = 'UNKNOWN',
    lambda_total,
    dd_fit   = 0,
    du_fit   = 0,
    dp_fit   = 0,
    np_fit   = 0,
    spf_immune  = false,
    dc_source   = 'SUPPLIER_INPUT',
    validated_by = null,
  } = supplierData;

  // ① 필수 필드 존재 확인
  if (typeof dd_fit !== 'number' || typeof du_fit !== 'number') {
    return { ok: false, error: 'dd_fit and du_fit must be numeric [FIT]', component_id };
  }
  if (dd_fit < 0 || du_fit < 0 || dp_fit < 0 || np_fit < 0) {
    return { ok: false, error: 'All FIT values must be ≥ 0', component_id };
  }

  // ② 합계 일관성 (lambda_total 제공 시)
  const sum_fit = dd_fit + du_fit + dp_fit + np_fit;
  if (typeof lambda_total === 'number' && lambda_total > 0) {
    const discrepancy = Math.abs(lambda_total - sum_fit) / lambda_total;
    if (discrepancy > 0.05) {
      return {
        ok: false,
        error: `lambda_total (${lambda_total}) vs sum of FITs (${sum_fit}) discrepancy > 5%`,
        component_id, discrepancy: +(discrepancy * 100).toFixed(1) + '%',
      };
    }
  }

  // ③ DC 산출
  const total_d = dd_fit + du_fit;
  const dc      = total_d > 0 ? +(dd_fit / total_d).toFixed(6) : 0;

  // ④ SPF 기여도 산출 (SPF Metric: λ_SPF / λ_total)
  //    SPF = 단일점 고장 = DU 고장 (진단 미검출 위험) — 면제 불가 시
  const spf_metric = (sum_fit > 0 && !spf_immune) ? +(du_fit / sum_fit).toFixed(6) : 0;

  // ⑤ LFM (잠재 고장 지표): (dp_fit + np_fit) / lambda_total
  const lfm = (sum_fit > 0) ? +((dp_fit + np_fit) / sum_fit).toFixed(6) : 0;

  return {
    ok: true,
    component_id,
    fmeda: {
      dd_fit,  du_fit,  dp_fit,  np_fit,
      lambda_total: typeof lambda_total === 'number' ? lambda_total : sum_fit,
      dc,             // 진단 커버리지
      spf_metric,     // 단일점 고장 지표
      lfm,            // 잠재 고장 지표
      spf_immune,
      dc_source:  dc_source + (validated_by ? `_${validated_by}` : ''),
      validated_by,
    },
    // calcDC_FMEDA()와 호환되는 comp 구조
    coverage: dc,
  };
}

// ══════════════════════════════════════════════════════════════════
// [Sprint-3] computeDFABeta — DFA β 계수 정량화
// ISO 26262-9 §8.4 / Annex D 결합인자(coupling factor) 기반
//
// β는 공통원인고장(CCF) 비율을 정량화:
//   β = P(공통원인 고장) / P(전체 고장)
//
// ISO 26262-9 Annex D: 6개 결합인자 → 각 항목 점수 합산 → β 결정
//
//  인자             점수 범위   의미
//  physical_sep     0-5        물리적 분리 (0=동일기판, 5=별도함체)
//  diversity        0-5        구현 다양성 (0=동일HW/SW, 5=완전 이종)
//  power_supply     0-3        전원 독립성 (0=공유, 3=완전 독립)
//  software         0-3        SW 독립성 (0=공유코드, 3=완전 다른 플랫폼)
//  signal_path      0-3        신호 경로 독립 (0=공유버스, 3=광링크 분리)
//  environment      0-2        환경 독립성 (0=동일 EMC환경, 2=격리)
//
//  총점 → β:
//   0~4   → β = 0.20 (독립성 매우 낮음, 공유 설계)
//   5~8   → β = 0.10 (부분 독립)
//   9~13  → β = 0.05 (중간 독립)
//  14~17  → β = 0.02 (높은 독립성)
//  18~21  → β = 0.01 (완전 독립, ASIL D 이중채널 목표)
// ══════════════════════════════════════════════════════════════════
export function computeDFABeta(couplingFactors = {}) {
  const {
    physical_sep = 0,   // [0-5]
    diversity    = 0,   // [0-5]
    power_supply = 0,   // [0-3]
    software     = 0,   // [0-3]
    signal_path  = 0,   // [0-3]
    environment  = 0,   // [0-2]
  } = couplingFactors;

  // 각 인자 범위 클램핑
  const s1 = Math.min(5, Math.max(0, physical_sep));
  const s2 = Math.min(5, Math.max(0, diversity));
  const s3 = Math.min(3, Math.max(0, power_supply));
  const s4 = Math.min(3, Math.max(0, software));
  const s5 = Math.min(3, Math.max(0, signal_path));
  const s6 = Math.min(2, Math.max(0, environment));
  const total = s1 + s2 + s3 + s4 + s5 + s6;  // 최대 21점

  // β 결정 룩업
  let beta, asil_achievable, description, descriptionEn;
  if      (total <= 4)  { beta = 0.20; asil_achievable = 'A';  description = '공유 설계 — CCF 위험 높음';   descriptionEn = 'Shared design — high CCF risk'; }
  else if (total <= 8)  { beta = 0.10; asil_achievable = 'B';  description = '부분 독립 — β 비진단 채널'; descriptionEn = 'Partial independence — β undiagnosed'; }
  else if (total <= 13) { beta = 0.05; asil_achievable = 'C';  description = '중간 독립 — 채널 간 다양성 확보'; descriptionEn = 'Medium independence — channel diversity'; }
  else if (total <= 17) { beta = 0.02; asil_achievable = 'D';  description = '높은 독립성 — β_D 진단 이중채널'; descriptionEn = 'High independence — β_D diagnostic dual-channel'; }
  else                  { beta = 0.01; asil_achievable = 'D+'; description = '완전 독립 — 최고 수준 이종 이중채널'; descriptionEn = 'Full independence — heterogeneous dual-channel'; }

  return {
    beta,
    total_score: total,
    max_score:   21,
    asil_achievable,
    description,  descriptionEn,
    factors: { physical_sep: s1, diversity: s2, power_supply: s3, software: s4, signal_path: s5, environment: s6 },
    // PFH 보정에 직접 사용 가능한 β_D (진단채널용 = beta), β (비진단 = min(2×beta, 0.20))
    beta_D: beta,
    beta_ND: Math.min(0.20, beta * 2),
  };
}

/**
 * FMEDA(Failure Mode Effects and Diagnostic Analysis) 데이터를 기반으로
 * 부품의 진단 커버리지(Diagnostic Coverage, DC)를 계산한다.
 *
 * DC = λ_DD / (λ_DD + λ_DU)   (ISO 26262-5 §8.4.5)
 *   λ_DD: 진단으로 검출된 위험 고장률 (Detected Dangerous FIT)
 *   λ_DU: 진단으로 미검출된 위험 고장률 (Undetected Dangerous FIT)
 *
 * FMEDA 데이터(dd_fit, du_fit)가 없으면 comp.coverage 하드코딩 설계값으로 폴백한다.
 *
 * @param {{ fmeda?: { dd_fit: number, du_fit: number }, coverage?: number }} comp  부품 정의 객체
 * @returns {number}  DC 값 (0.0 ~ 1.0). FMEDA 미존재 시 comp.coverage || 0 반환.
 */
function calcDC_FMEDA(comp) {
  const f = comp.fmeda;
  if (f && typeof f.dd_fit === 'number' && typeof f.du_fit === 'number') {
    const total_d = f.dd_fit + f.du_fit;
    if (total_d > 0) {
      return +(f.dd_fit / total_d).toFixed(6);  // DC = λ_DD / (λ_DD + λ_DU)
    }
  }
  return comp.coverage || 0;  // 폴백: 하드코딩 설계 의도값 (FMEDA 데이터 미존재 시)
}

// [Fix-DC-FMEDA-1] dc_source 추적 — 감사 패키지용 출처 표기
/**
 * 부품 DC 값의 출처를 문자열로 반환한다. 감사 추적성(Audit Traceability) 지원용.
 *
 * FMEDA 데이터(fmeda.dd_fit)가 존재하면 'FMEDA_CALC' 또는 fmeda.dc_source를 반환하고,
 * 없으면 'HARDCODED_DESIGN_INTENT'를 반환한다.
 *
 * @param {{ fmeda?: { dd_fit?: number, dc_source?: string } }} comp  부품 정의 객체
 * @returns {'FMEDA_CALC'|'HARDCODED_DESIGN_INTENT'|string}  DC 출처 식별자
 */
function getDC_source(comp) {
  if (comp.fmeda && typeof comp.fmeda.dd_fit === 'number') {
    return comp.fmeda.dc_source || 'FMEDA_CALC';
  }
  return 'HARDCODED_DESIGN_INTENT';
}

// ══════════════════════════════════════════════════════════════════
// [Fix-RPN-ASIL-1] ASIL-aware Action Priority (AP) 체계
// 출처: AIAG-VDA FMEA 2019 §7.8 + ISO 26262 ASIL 연계 확장
//
// 문제: 종전 RPN > 200 → HIGH, > 100 → MEDIUM 단일 임계값은
//       ASIL D 항목(고위험)과 QM 항목에 동일 기준 적용 — ISO 26262 위배
//
// 수정: ASIL 등급별 AP-H/M/L 임계값 차등 적용
//   - ASIL D: RPN > 100 → AP-H  (ASIL D는 낮은 RPN도 즉시 대응 필요)
//   - ASIL C: RPN > 125 → AP-H
//   - ASIL B: RPN > 150 → AP-H
//   - ASIL A / QM: RPN > 200 → AP-H (AIAG 4판 기존 기준 유지)
// ══════════════════════════════════════════════════════════════════
const RPN_AP_TABLE = {
  // { H: AP-H 임계값, M: AP-M 임계값 }  → RPN > H : AP-H, > M : AP-M, else AP-L
  D:  { H: 100, M:  50 },  // ASIL D — 가장 엄격
  C:  { H: 125, M:  63 },  // ASIL C
  B:  { H: 150, M:  75 },  // ASIL B
  A:  { H: 200, M: 100 },  // ASIL A — AIAG 4판 기준
  QM: { H: 200, M: 100 },  // QM    — AIAG 4판 기준 (기존 동일)
};

// [Fix-RPN-ASIL-1] calcActionPriority — ASIL 연계 AP 산출
/**
 * ASIL 등급에 연계된 Action Priority(AP)를 산출한다.
 *
 * AIAG-VDA FMEA 2019 §7.8의 AP-H/M/L 체계를 ISO 26262 ASIL과 연계하여 확장했다.
 * ASIL 등급이 높을수록 낮은 RPN에서도 AP-H가 부여되어 즉시 대응을 요구한다:
 *   ASIL D: RPN > 100 → AP-H (ASIL D는 낮은 RPN도 즉시 대응)
 *   ASIL C: RPN > 125 → AP-H
 *   ASIL B: RPN > 150 → AP-H
 *   ASIL A/QM: RPN > 200 → AP-H (AIAG 기존 기준 유지)
 *
 * @param {number} RPN   위험 우선순위 수 (Risk Priority Number, 1~1000)
 * @param {string} asil  ASIL 등급 ('D'|'C'|'B'|'A'|'QM'). 미정의 시 'QM'으로 폴백.
 * @returns {{ ap, risk_level, action_required, action_requiredEn, threshold_H, threshold_M, asil_applied }}
 */
function calcActionPriority(RPN, asil) {
  const safeAsil = (asil && RPN_AP_TABLE[asil]) ? asil : 'QM';
  const thr      = RPN_AP_TABLE[safeAsil];
  let ap, risk_level, action_required, action_requiredEn;

  if (RPN > thr.H) {
    ap = 'AP-H'; risk_level = 'HIGH';
    action_required   = 'AP-H: 즉시 대응';
    action_requiredEn = 'AP-H: Immediate Action';
  } else if (RPN > thr.M) {
    ap = 'AP-M'; risk_level = 'MEDIUM';
    action_required   = 'AP-M: 계획 대응';
    action_requiredEn = 'AP-M: Planned Action';
  } else {
    ap = 'AP-L'; risk_level = 'LOW';
    action_required   = 'AP-L: 모니터링';
    action_requiredEn = 'AP-L: Monitoring';
  }

  return {
    ap, risk_level,
    action_required, action_requiredEn,
    threshold_H:    thr.H,          // 감사 추적성: 어떤 임계값이 적용됐는지 기록
    threshold_M:    thr.M,
    asil_applied:   safeAsil,       // 적용된 ASIL 등급 명시
  };
}

// ══════════════════════════════════════════════════════════════════
// calcPFH — IEC 61508-6 / ISO 26262-5 아키텍처별 PFH 계산
// arch: '1oo1' | '1oo2' | '1oo2D'
// T_proof: 정기 점검 주기(시간), 기본값 8760h(1년)
// β: CCF(공통원인고장) 인수
// ══════════════════════════════════════════════════════════════════
/**
 * IEC 61508-6 / ISO 26262-5에 따라 시스템 아키텍처별 위험 고장 빈도(PFH)를 계산한다.
 *
 * PFH(Probability of Failure per Hour)는 ASIL 목표값 충족 여부를 판단하는 핵심 지표다:
 *   ASIL D: PFH < 1×10⁻⁸ /h
 *   ASIL C: PFH < 1×10⁻⁷ /h
 *   ASIL B: PFH < 1×10⁻⁷ /h
 *   ASIL A: PFH < 1×10⁻⁶ /h
 *
 * 아키텍처별 계산식 (λ_DU = λ × (1 - DC)):
 *   1oo1: PFH = λ_DU                                 (단일채널)
 *   1oo2: PFH = (1-β)·λ_DU²·T_proof + β·λ_DU       (이중채널, β=0.10)
 *   1oo2D: PFH = (1-β_D)·λ_DU²·T_proof + β_D·λ_DU  (이중채널+진단, β_D=0.02)
 *
 * @param {{ lambda_h: number, architecture?: '1oo1'|'1oo2'|'1oo2D', fmeda?: object, coverage?: number }} comp  부품 정의
 * @param {number} [T_proof=8760]  정기 점검 주기 (시간). 기본 1년 = 8760h.
 * @returns {number}  PFH 값 (1/h 단위)
 */
function calcPFH(comp, T_proof = 8760) {
  const lambda   = comp.lambda_h || 0;
  // [Fix-DC-FMEDA-1] FMEDA 기반 DC 우선 사용
  const DC       = calcDC_FMEDA(comp);
  const arch     = comp.architecture || '1oo1';
  const lambda_DU = lambda * (1 - DC);   // 비검출 위험 고장률

  switch (arch) {
    case '1oo2D': {
      // IEC 61508-6 Table B.9 — 1oo2D (채널간 진단 포함)
      // PFH ≈ (1-β_D)×λ_DU²×T_proof + β_D×λ_DU
      const beta_D = 0.02;  // ISO 26262-9 권고 β_D (진단 이중채널)
      return (1 - beta_D) * lambda_DU * lambda_DU * T_proof + beta_D * lambda_DU;
    }
    case '1oo2': {
      // IEC 61508-6 Table B.8 — 1oo2 (채널간 진단 없음)
      // PFH ≈ (1-β)×λ_DU²×T_proof + β×λ_DU
      const beta = 0.10;    // ISO 26262-9 권고 β (비진단 이중채널)
      return (1 - beta) * lambda_DU * lambda_DU * T_proof + beta * lambda_DU;
    }
    case '1oo1':
    default:
      // 단일 채널: PFH = λ_DU
      return lambda_DU;
  }
}

// ── ASIL Lookup Tables (ISO 26262 Part 3, Table B.1) ──
const ASIL_TABLE = {
  // S=0: no injury → QM always
  "0-1-1":"QM","0-1-2":"QM","0-1-3":"QM",
  "0-2-1":"QM","0-2-2":"QM","0-2-3":"QM",
  "0-3-1":"QM","0-3-2":"QM","0-3-3":"QM",
  "0-4-1":"QM","0-4-2":"QM","0-4-3":"QM",
  // S=1: light injury
  "1-1-1":"QM","1-1-2":"QM","1-1-3":"QM",
  "1-2-1":"QM","1-2-2":"QM","1-2-3":"A",
  "1-3-1":"QM","1-3-2":"A", "1-3-3":"B",
  "1-4-1":"QM","1-4-2":"A", "1-4-3":"B",
  // S=2: severe/life-threatening injury
  "2-1-1":"QM","2-1-2":"QM","2-1-3":"A",
  "2-2-1":"QM","2-2-2":"A", "2-2-3":"B",
  "2-3-1":"A", "2-3-2":"B", "2-3-3":"C",
  "2-4-1":"A", "2-4-2":"B", "2-4-3":"C",
  // S=3: fatal
  "3-1-1":"A", "3-1-2":"B", "3-1-3":"C",
  "3-2-1":"B", "3-2-2":"B", "3-2-3":"C",
  "3-3-1":"B", "3-3-2":"C", "3-3-3":"D",
  "3-4-1":"C", "3-4-2":"C", "3-4-3":"D",
};

const ASIL_SCORE = { QM: 0, A: 1, B: 2, C: 3, D: 4 };

const ASIL_COLOR = { QM: "#64748b", A: "#22c55e", B: "#eab308", C: "#f97316", D: "#ef4444" };

// FTA_TOP_EVENTS, FTA_INTERMEDIATE_EVENTS → constants/FTA_EVENTS.js 에서 import

// ── calcASIL ──
/**
 * ISO 26262 Part 3 Table B.1에 따라 ASIL 등급을 결정한다.
 *
 * 세 파라미터의 조합으로 안전무결성수준(ASIL A~D 또는 QM)을 반환한다:
 *   S (Severity):      0=상해없음, 1=경상, 2=중증/생명위협, 3=사망
 *   E (Exposure):      1=매우낮음, 2=낮음, 3=보통, 4=높음
 *   C (Controllability): 1=보통제어가능, 2=어려움, 3=거의불가
 *
 * [Fix-ASIL-1] 수정 사항: ASIL_TABLE[key] || "D" 패턴에서 유효 범위 검사로 변경.
 * 범위를 벗어난 입력은 ASIL D로 오지정하지 않고 null을 반환하여 호출부에서 처리한다.
 *
 * @param {0|1|2|3} S   Severity 등급
 * @param {1|2|3|4} E   Exposure 등급
 * @param {1|2|3}   C   Controllability 등급
 * @returns {'QM'|'A'|'B'|'C'|'D'|null}  ASIL 등급. 유효하지 않은 입력이면 null.
 */
// [Fix-ASIL-1] 미정의 키 → "D" 기본값 오류 수정 (ISO 26262 Part 3 Table B.1)
//
// 문제: ASIL_TABLE[key] || "D" 패턴은 테이블에 없는 입력값(범위 초과, 타입 오류)을
//       ASIL D로 과대 지정함. S=0, E=4, C=1 같은 QM 조합도 D로 잘못 반환.
//
// 수정:
//   1) 유효 범위 검사: S∈{0,1,2,3}, E∈{1,2,3,4}, C∈{1,2,3}
//   2) 범위 초과 또는 비정수 입력 → null 반환 + console.error 경고
//   3) 테이블 키 미존재(정상적으로 발생하지 않아야 하는 방어) → null 반환
//   4) 호출부에서 null 처리: calcASIL() ?? "QM" 패턴으로 안전 폴백 적용
//
// 유효 범위 (ISO 26262-3 Table B.1):
//   S : 0 (상해없음) ~ 3 (생명위협)
//   E : 1 (매우낮음) ~ 4 (높음)   ※ E=0은 표준에 없음
//   C : 1 (제어가능) ~ 3 (제어불능)
const ASIL_VALID_S = new Set([0, 1, 2, 3]);
const ASIL_VALID_E = new Set([1, 2, 3, 4]);
const ASIL_VALID_C = new Set([1, 2, 3]);

function calcASIL(S, E, C) {
  // 1) 타입/범위 검사
  if (!ASIL_VALID_S.has(S) || !ASIL_VALID_E.has(E) || !ASIL_VALID_C.has(C)) {
    console.error(
      `[calcASIL] 유효하지 않은 입력: S=${S}, E=${E}, C=${C}. ` +
      `유효 범위: S∈{0-3}, E∈{1-4}, C∈{1-3}. null 반환.`
    );
    return null;
  }
  // 2) 테이블 조회 — 범위가 유효하면 반드시 키가 존재해야 함 (방어적 검사)
  const key = `${S}-${E}-${C}`;
  const result = ASIL_TABLE[key];
  if (result === undefined) {
    console.error(`[calcASIL] 테이블 누락 키: ${key}. null 반환. ASIL_TABLE 점검 필요.`);
    return null;
  }
  return result;
}

/**
 * calcASIL의 안전 래퍼(safe wrapper). null 반환 시 'QM'으로 폴백한다.
 *
 * calcASIL()이 유효하지 않은 입력에 대해 null을 반환할 수 있으므로,
 * 항상 문자열을 요구하는 호출부에서 `calcASIL_safe(S,E,C)`를 사용한다.
 * null 반환은 보수적(conservative)으로 'QM'(최저 요구사항)으로 처리한다.
 *
 * @param {number} S  Severity
 * @param {number} E  Exposure
 * @param {number} C  Controllability
 * @returns {'QM'|'A'|'B'|'C'|'D'}  항상 유효한 ASIL 문자열 반환
 */
// calcASIL 안전 래퍼 — null을 QM으로 처리하는 호출부 단축 유틸
// 사용: calcASIL_safe(S, E, C) → 항상 문자열 반환 ("QM"~"D")
function calcASIL_safe(S, E, C) {
  return calcASIL(S, E, C) ?? "QM";
}

// ── computeFMEA ──
/**
 * 고장 모드 목록과 부품 FIT 데이터를 분석하여 FMEA(Failure Mode and Effects Analysis) 결과를 생성한다.
 *
 * ISO 26262 Part 8 및 AIAG-VDA FMEA 2019 방법론을 적용하여 각 고장 모드별로
 * 심각도(S), 발생빈도(O), 검출도(D), RPN, 잔류 RPN, PFH, ASIL 연계 AP를 계산한다.
 *
 * 적용된 수정 사항:
 *   [Fix-CAT-1] FM_COMPONENT_MAP으로 FM ID → 정확한 부품 직접 조회 (category 중복 문제 해결)
 *   [Fix-SEV-1] ASIL_S_TO_FMEA_SEV 대응표: S=3 → 심각도 10 (구 9에서 수정)
 *   [Fix-DET-1] FM_DETECTION_D 전문가 검토 테이블로 검출도 결정
 *   [Fix-DC-FMEDA-1] FMEDA dd_fit/(dd_fit+du_fit) 기반 DC 산출
 *   [Fix-RPN-ASIL-1] ASIL 등급별 차등 AP 임계값 적용 (AIAG-VDA 2019 §7.8)
 *
 * @param {object[]} faultModes     고장 모드 정의 배열 (constants/FAULT_MODES.js)
 * @param {object[]} componentFIT   부품 FIT/lambda_h/아키텍처 정의 배열
 * @param {object}   params         차량 파라미터 객체
 * @returns {object[]}  고장 모드별 FMEA 결과 배열
 *   각 항목: { severity, occurrence, detection, RPN, RPN_residual, PFH, ap, risk_level, ... }
 */
// [Fix-CAT-1]       FM_COMPONENT_MAP으로 FM ID → 정확한 C-xx 직접 조회
//                   동일 category 내 복수 컴포넌트가 있어도 항상 올바른 컴포넌트 참조
// [Fix-SEV-1]       ASIL_S_TO_FMEA_SEV 룩업 테이블: S=3 → 9 오산정 수정 (→ 10)
// [Fix-DET-1]       FM_DETECTION_D 전문가 검토 테이블: 실제 검출 관리 반영
// [Fix-DC-FMEDA-1]  DC = calcDC_FMEDA() — FMEDA dd_fit/(dd_fit+du_fit) 산출
//                   (종전 coverage 하드코딩 → FMEDA 기반으로 교체)
// [Fix-RPN-ASIL-1]  calcActionPriority() — ASIL 연계 AP-H/M/L (AIAG-VDA 2019 §7.8)
function computeFMEA(faultModes, componentFIT, params) {
  // [Fix-CAT-1] id 기반 컴포넌트 맵 구성 (category 중복 문제 해결)
  const compById = {};
  componentFIT.forEach(c => { compById[c.id] = c; });
  // fallback용 category 맵 (FM_COMPONENT_MAP에 없는 신규 FM 대응)
  const compByCategory = {};
  componentFIT.forEach(c => { compByCategory[c.category] = compByCategory[c.category] || c; });

  return faultModes.map(fm => {
    // [Fix-SEV-1] ASIL S 0~3 → FMEA Severity 1~10 공식 대응표
    const severity = ASIL_S_TO_FMEA_SEV[fm.S] ?? Math.min(10, Math.round(fm.S * 2.5 + 1.5));

    // Occurrence: 확률(prob) 기반 1~10 스케일 (변경 없음)
    const log_prob = fm.prob > 0 ? -Math.log10(fm.prob) : 8;
    const occurrence = Math.max(1, Math.min(10, Math.round(11 - log_prob * 0.7)));

    // [Fix-CAT-1] FM_COMPONENT_MAP → compById 순으로 정확한 컴포넌트 조회
    const mappedCompId = FM_COMPONENT_MAP[fm.id];
    const comp = (mappedCompId && compById[mappedCompId])
                  || compByCategory[fm.category]
                  || componentFIT[0];

    // [Fix-DC-FMEDA-1] FMEDA 기반 DC 산출 (ISO 26262-5 §8.4.5)
    //   종전: comp.coverage || 0.95 — 하드코딩 설계 의도값 직접 사용
    //   수정: calcDC_FMEDA()로 fmeda.dd_fit / (dd_fit + du_fit) 계산
    //         fmeda 미존재 시 comp.coverage 자동 폴백
    const coverage  = calcDC_FMEDA(comp);
    const dc_source = getDC_source(comp);  // 감사 추적성: DC 출처 기록

    // [Fix-DET-1] FM_DETECTION_D 전문가 검토값 우선 사용
    const detection = FM_DETECTION_D[fm.id]
                      ?? Math.max(1, Math.min(10, Math.round((1 - coverage) * 9 + 1))); // [B-4 Fix] factor 9: coverage→D 선형(0→10, 1→1); 구 factor 50은 coverage≥0.82에서 D=10 포화

    const RPN      = severity * occurrence * detection;
    const RPN_norm = +(RPN / 1000 * 100).toFixed(1);

    // 안전 메커니즘 대응 후 잔류 RPN — [B-5 Fix] coverage 비율 기반 감소
    // 구: occurrence −round(coverage×3), detection −2 고정 → 안전 메커니즘 효과 과대/과소 추정
    // 수정: residual_occ = occ × (1−coverage), residual_det = det × (1−coverage×0.5)
    //       coverage=1.0 → occ→1(최소), det→det×0.5 (최소 50% 감소)
    //       coverage=0.0 → 감소 없음 (원본 유지)
    const residual_occ = Math.max(1, Math.round(occurrence * (1 - coverage)));
    const residual_det = Math.max(1, Math.round(detection  * (1 - coverage * 0.5)));
    const RPN_residual = severity * residual_occ * residual_det;

    // 부품 FIT / lambda_h / PFH
    const FIT_val  = comp.FIT       || 100;
    const lambda_h = comp.lambda_h  || 1e-7;
    const PFH      = calcPFH(comp);

    // [Fix-RPN-ASIL-1] ASIL-aware Action Priority (AIAG-VDA FMEA 2019 §7.8 연계)
    //   종전: RPN > 200 → HIGH / > 100 → MEDIUM — ASIL 무관 단일 임계값
    //   수정: ASIL D → H:100/M:50, C → H:125/M:63, B → H:150/M:75, A/QM → H:200/M:100
    const fmItemAsil = fm.asil || 'QM';
    const apResult   = calcActionPriority(RPN, fmItemAsil);

    return {
      ...fm,
      severity, occurrence, detection, RPN, RPN_norm,
      RPN_residual: +RPN_residual.toFixed(0),
      FIT: FIT_val, lambda_h, coverage, PFH: +PFH.toExponential(2),
      dc_source,                          // [Fix-DC-FMEDA-1] DC 산출 출처
      comp_id: comp.id,                   // 추적성: 어떤 컴포넌트가 사용됐는지 출력
      // [Fix-RPN-ASIL-1] ASIL 연계 AP 필드
      ap:               apResult.ap,
      risk_level:       apResult.risk_level,
      action_required:  apResult.action_required,
      action_requiredEn:apResult.action_requiredEn,
      ap_threshold_H:   apResult.threshold_H,  // 감사 추적: 적용된 임계값
      ap_threshold_M:   apResult.threshold_M,
      asil_applied:     apResult.asil_applied,  // 감사 추적: 적용된 ASIL
    };
  });
}

// ── runFaultInjectionSeries ──
/**
 * 모든 고장 모드에 대해 순차적으로 고장 주입 시뮬레이션을 실행한다.
 *
 * 각 고장 모드별 runFaultSimulation()을 호출하고, 진행 상황을 onProgress 콜백으로 보고한다.
 * 합격(pass) 기준: 고장 모드 시뮬레이션에서 임계값 초과율 < 5%.
 *
 * @param {object[]} faultModes    고장 모드 정의 배열
 * @param {object}   params        차량 파라미터 (fault_injection_n: 반복 횟수)
 * @param {Function|null} onProgress  (completed, total, fm_id) => void  진행 콜백
 * @returns {{ results, pass_count, fail_count, total, overall_pass_rate, test_time_ms }}
 */
function runFaultInjectionSeries(faultModes, params, onProgress) {
  const t0 = performance ? performance.now() : Date.now();
  const results = [];
  for (let i = 0; i < faultModes.length; i++) {
    const fm = faultModes[i];
    const res = runFaultSimulation(fm, params, params.fault_injection_n || 300);
    results.push({ fm_id: fm.id, fm_name: fm.name, fm_nameEn: fm.nameEn || fm.name, category: fm.category, categoryEn: fm.categoryEn || fm.category,
                   asil: fm.asil || 'QM', ...res });
    if (onProgress) onProgress(i + 1, faultModes.length, fm.id);
  }
  const pass_count  = results.filter(r => r.pass).length;
  const fail_count  = results.filter(r => !r.pass).length;
  const test_time_ms = (performance ? performance.now() : Date.now()) - t0;
  return {
    results,
    pass_count,
    fail_count,
    total: faultModes.length,
    overall_pass_rate: +(pass_count / faultModes.length * 100).toFixed(1),
    test_time_ms: +test_time_ms.toFixed(1),
  };
}

// ── runFaultSimulation ──
/**
 * 단일 고장 모드에 대해 몬테카를로 고장 주입 시뮬레이션을 수행한다.
 *
 * 각 반복마다 차량 파라미터를 ±10% 섭동한 뒤 고장 모드별 물리 기반 메트릭을 계산하고,
 * 임계값(fm.threshold) 초과 여부를 판정한다.
 *
 * 고장 모드별 물리 모델:
 *   apsBiasFault/motorTorqueUAFault: 비의도 가속 → 토크 지연(ms) / 제동거리 초과(m)
 *   thermalRunawayFault: Arrhenius 열폭주 모델 (T_start ∈ [45,80°C], P_heat/C_th → °C/s)
 *   steeringFault: SAS 편차 → 횡방향 이탈 거리 (d_lat ≈ v·t·tan(θ))
 *   epsOverFault: EPS 과토크 → 정규 분포 기반 토크 추정 (35~65Nm × 부하계수)
 *   epbFault: 경사 가속도 × 롤어웨이 시간 → 이탈 속도 (km/h)
 *
 * 합격 기준: 임계값 초과율(fail_rate) < 5.0%
 *
 * @param {{ id, fault_fn, threshold, sim_metric, duration_s }} fm  고장 모드 정의
 * @param {object} params   차량 파라미터
 * @param {number} n        시뮬레이션 반복 횟수 (기본값: params.simulation_per_hazard || 500)
 * @returns {{ N, mean, std, p95, p99, fail_rate, fail_count, hist, pass, safety_margin }}
 */
function runFaultSimulation(fm, params, n) {
  const N = n || params.simulation_per_hazard || 500;
  const results = [];
  for (let i = 0; i < N; i++) {
    // 파라미터 섭동 (±10%)
    const m_v = params.m * (0.95 + Math.random() * 0.10);
    const v0 = (params.hara_vehicle_speed_kmh || 120) * (0.85 + Math.random() * 0.30);
    const duration = fm.duration_s * (0.5 + Math.random() * 1.0);
    let metric_val;

    // 고장 모드별 물리 기반 메트릭 계산
    if (fm.fault_fn === "apsBiasFault" || fm.fault_fn === "motorTorqueUAFault") {
      // 비의도 가속 → 토크 지연 + 제동거리
      const T_delay_ms = 50 + Math.random() * 300; // 50~350ms 랜덤
      const v_mps = v0 / 3.6;
      const decel_g = 0.8 + Math.random() * 0.2;
      const d_extra = v_mps * (T_delay_ms / 1000) + 0.5 * v_mps * (T_delay_ms / 1000);
      metric_val = fm.sim_metric === "max_torque_delay_ms" ? T_delay_ms : d_extra;
    } else if (fm.fault_fn === "canDelayFault") {
      metric_val = 20 + Math.random() * 150; // ms
    } else if (fm.fault_fn === "contactorFault") {
      metric_val = 10 + Math.random() * 80; // ms
    } else if (fm.fault_fn === "battOVFault" || fm.fault_fn === "battUVFault") {
      metric_val = 20 + Math.random() * 120; // ms detection
    } else if (fm.fault_fn === "thermalRunawayFault") {
      // [A68-07-Fix-FS2] Arrhenius 열폭주 모델 수정
      // 이전: k_TR * duration * 60 / duration = k_TR * 60 → duration 약분 → 0.01~0.04°C/s (임계 5°C/s 미달)
      // 수정: T_start ∈ [45,80°C], 임계 교차 T≈66.5°C → fail_rate≈40%
      const T_start = 45 + Math.random() * 35; // 45~80°C (현실적 배터리 온도 범위)
      const P_heat  = 0.05 * Math.exp((T_start - 55) / 5); // W/g equivalent (Arrhenius)
      const C_th    = 0.1;  // J/(g·K) 셀 열용량
      metric_val    = P_heat / C_th; // °C/s
    } else if (fm.fault_fn === "coolingFault") {
      // 온도 상승
      const Q = (params.P_max_wheel || 250000) * 0.15; // 15% loss → heat
      const C_th = 50000; // J/°C system
      metric_val = Q * duration / C_th;
    } else if (fm.fault_fn === "regenLossFault" || fm.fault_fn === "absDelayFault") {
      const v_mps = v0 / 3.6;
      const decel_loss = 0.2 + Math.random() * 0.3; // g reduction
      metric_val = fm.sim_metric === "braking_dist_increase_m"
        ? v_mps * v_mps / (2 * decel_loss * 9.81) - v_mps * v_mps / (2 * 0.8 * 9.81)
        : 5 + Math.random() * 15;
    } else if (fm.fault_fn === "igbtShortFault") {
      metric_val = 400 + Math.random() * 600; // A peak
    } else if (fm.fault_fn === "vdcOVFault") {
      metric_val = 430 + Math.random() * 60; // V
    } else if (fm.fault_fn === "socErrorFault") {
      metric_val = 2 + Math.random() * 10; // %
    } else if (fm.fault_fn === "escMalfunctionFault" || fm.fault_fn === "torqueVectoringFault") {
      metric_val = 0.05 + Math.random() * 0.3; // rad/s yaw error
    } else if (fm.fault_fn === "liPlatingFault") {
      metric_val = Math.floor(Math.random() * 6); // risk score 0-5
    } else if (fm.fault_fn === "insulationFault") {
      metric_val = 30 + Math.random() * 200; // Ω/V
    } else if (fm.fault_fn === "steeringFault") {
      // [A68-07-Fix-FS3] SAS 편차 기반 횡방향 이탈 모델
      // 이전: default fallback (threshold×[0.3,1.8]) → 물리 의미 없음
      // 수정: 조향각 오차 → 횡방향 이탈 거리 추정
      const steer_err_deg = 2 + Math.random() * 8; // 2~10° SAS 편차
      const v_mps = v0 / 3.6;
      // d_lat ≈ v × duration × tan(err_deg) — 소각도 근사
      metric_val = v_mps * fm.duration_s * Math.tan(steer_err_deg * Math.PI / 180);
    } else if (fm.fault_fn === "epsOverFault") {
      // [A68-07-Fix-FS3] EPS 과토크 — 정규 분포 기반 (임계 50Nm 근방)
      // 이전: default fallback → threshold×[0.3,1.8] = 15~90Nm 균등
      // 수정: 물리 기반 EPS 토크 분포 (평균 40Nm, 표준편차 15Nm)
      const eps_base = 35 + Math.random() * 30; // 35~65 Nm
      const load_factor = 0.8 + Math.random() * 0.4; // 부하 변동
      metric_val = eps_base * load_factor;
    } else if (fm.fault_fn === "epbFault") {
      // [A68-07-Fix-FS3] 경사도 기반 EPB Rollaway 속도 모델
      // 이전: default fallback → threshold×[0.3,1.8] = 1.5~9 km/h 균등
      // 수정: 경사 가속도 × 롤어웨이 시간 → 속도 km/h
      const grade_deg = (params.grade_deg || 5) * (0.5 + Math.random() * 1.5);
      const g_accel = Math.sin(grade_deg * Math.PI / 180) * 9.81; // m/s²
      const t_rollaway = 2 + Math.random() * 5; // 2~7초
      metric_val = g_accel * t_rollaway * 3.6; // km/h
    } else {
      metric_val = fm.threshold * (0.3 + Math.random() * 1.5);
    }
    results.push({ val: metric_val, fail: metric_val > fm.threshold });
  }
  const vals = results.map(r => r.val);
  const mean = vals.reduce((s,v)=>s+v,0)/N;
  const std  = Math.sqrt(vals.reduce((s,v)=>s+(v-mean)**2,0)/N);
  const sorted = [...vals].sort((a,b)=>a-b);
  const p95   = sorted[Math.floor(N*0.95)];
  const p99   = sorted[Math.floor(N*0.99)];
  const fail_count = results.filter(r=>r.fail).length;
  const fail_rate  = +(fail_count/N*100).toFixed(1);
  // Histogram 20 bins
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const bw = (hi-lo)/20||1;
  const hist = Array.from({length:20},(_,i)=>({
    x: +(lo+(i+0.5)*bw).toFixed(2),
    count: vals.filter(v=>v>=lo+i*bw&&v<lo+(i+1)*bw).length,
  }));
  return { N, mean:+mean.toFixed(2), std:+std.toFixed(2), p95:+p95.toFixed(2), p99:+p99.toFixed(2),
    fail_rate, fail_count, hist,
    pass: fail_rate < 5.0,   // < 5% 초과율 기준
    safety_margin: +((fm.threshold - mean)/Math.max(std,0.001)).toFixed(2) };
}

// ── computeSOTIF ──
/**
 * ISO 21448:2022(SOTIF) 기반 인지·판단 성능 분석을 수행한다.
 *
 * SOTIF(Safety Of The Intended Functionality)는 기능 부족 또는 예측 불가 사용자 행동으로
 * 인한 비합리적 위험을 다루며, ISO 26262 기능안전과 보완 관계를 갖는다.
 *
 * 분석 항목:
 *   1. OEDR 성능: base_oedr × 날씨 계수 × 속도 계수 × 온도 계수
 *      (사용자 정의 base_oedr_override 및 sensor_factors_override 지원)
 *   2. ODD 커버리지: Speed × Weather × Lighting 3차원 매트릭스 (총 64셀)
 *      커버리지 기준: 셀별 복합 계수 ≥ 0.80
 *   3. Known Scenarios: 16종 시나리오별 OEDR 계산 (KS-01~KS-16, [RevB] 확장)
 *   4. Unknown Risk: 통계 모델(50%) + ODD 미커버 비율(50%) 블렌딩
 *   5. SOTIF 점수: 100점 만점 종합 평가
 *
 * 인증 기준 (ISO 21448:2022):
 *   Known Scenario 전체 통과(OEDR≥0.99) AND SOTIF score≥80
 *
 * @param {object} params  차량 파라미터
 *   params.hara_vehicle_speed_kmh  - 대표 속도 (기본 120km/h)
 *   params.T_amb                   - 주변 온도 (기본 25°C)
 *   params.road_condition          - 노면 조건 ('dry'|'wet'|'snow'|'ice')
 *   params.base_oedr_override      - 사용자 정의 OEDR 기준값 (0.50~1.00)
 *   params.sensor_factors_override - { weather, speed, temp } 열화 계수 직접 입력
 *   params.odd_dims                - ODD 차원 오버라이드 { speed, weather, lighting, road }
 * @returns {object}  SOTIF 분석 결과 (oedr_perf, sotif_score, known_scenarios, coverage_pct 등)
 */
function computeSOTIF(params) {
  const v_kmh   = params.hara_vehicle_speed_kmh || 120;
  const T_amb   = params.T_amb || 25;
  const weather = params.road_condition || 'dry';

  // 센서 성능 열화 모델 (날씨·속도·온도)
  const weather_factor = { dry:1.0, wet:0.78, snow:0.52, ice:0.45 }[weather] || 0.8;
  const speed_factor   = Math.max(0.3, 1 - (v_kmh - 60) / 200);
  const temp_factor    = T_amb < 0 ? 0.65 : T_amb < 10 ? 0.82 : 1.0;
  // [MED-5] base_oedr — 사용자 정의 센서 기준 성능 입력 지원 (ISO 21448 §8)
  // params.base_oedr_override: UI에서 주입되는 사용자 정의 값 (0.50~1.00)
  // 미입력 시 기존 기본값 0.88 유지 (하위 호환)
  // params.sensor_factors_override: { weather, speed, temp } — 개별 열화 계수 직접 입력 가능
  const base_oedr = (typeof params.base_oedr_override === 'number'
                     && params.base_oedr_override >= 0.5
                     && params.base_oedr_override <= 1.0)
    ? params.base_oedr_override
    : 0.88;
  const _sf_ov = params.sensor_factors_override || {};
  const _wf = (typeof _sf_ov.weather === 'number') ? Math.max(0.1, Math.min(1.0, _sf_ov.weather)) : weather_factor;
  const _spf = (typeof _sf_ov.speed   === 'number') ? Math.max(0.1, Math.min(1.0, _sf_ov.speed))   : speed_factor;
  const _tf  = (typeof _sf_ov.temp    === 'number') ? Math.max(0.1, Math.min(1.0, _sf_ov.temp))    : temp_factor;
  const oedr_perf = +(base_oedr * _wf * _spf * _tf).toFixed(3);

  // ── [Sprint-3] ODD 차원 정의 — 사용자 정의 입력 지원 ────────────────────
  // params.odd_dims로 전체 또는 일부 차원 오버라이드 가능 (ISO 21448:2022 §5)
  //
  // 오버라이드 방법:
  //   computeSOTIF({
  //     odd_dims: {
  //       speed: [{ id:'city', label:'Urban <30', freq:0.60, cov_base:0.95 }, ...],
  //       weather: undefined,   // undefined 차원은 기본값 유지
  //     }
  //   })
  //
  // 각 항목 스키마:
  //   speed/road: { id, label, freq(비중합 1.0), cov_base(기저 커버리지 0-1) }
  //   weather/lighting: { id, label, factor(성능 배율 0-1), freq(비중합 1.0) }
  const ODD_DEFAULTS = {
    speed: [
      { id:'city',     label:'City 0–50 km/h',    freq:0.35, cov_base:0.92 },
      { id:'suburban', label:'Suburban 50–100',    freq:0.30, cov_base:0.85 },
      { id:'highway',  label:'Highway 100–130',    freq:0.25, cov_base:0.76 },
      { id:'high_spd', label:'High-speed >130',    freq:0.10, cov_base:0.58 },
    ],
    weather: [
      { id:'clear', label:'Clear',  factor:1.00, freq:0.55 },
      { id:'rain',  label:'Rain',   factor:0.74, freq:0.25 },
      { id:'snow',  label:'Snow',   factor:0.50, freq:0.12 },
      { id:'fog',   label:'Fog',    factor:0.42, freq:0.08 },
    ],
    lighting: [
      { id:'day',    label:'Daytime',  factor:1.00, freq:0.55 },
      { id:'dusk',   label:'Dusk/Dawn',factor:0.80, freq:0.10 },
      { id:'night',  label:'Night',    factor:0.65, freq:0.30 },
      { id:'tunnel', label:'Tunnel',   factor:0.70, freq:0.05 },
    ],
    road: [
      { id:'motorway', label:'Motorway', factor:0.90, freq:0.30 },
      { id:'urban',    label:'Urban',    factor:0.78, freq:0.40 },
      { id:'rural',    label:'Rural',    factor:0.72, freq:0.20 },
      { id:'parking',  label:'Parking', factor:0.82, freq:0.10 },
    ],
  };

  // 사용자 정의 ODD 차원 병합 (차원 단위 오버라이드)
  const _userOdd = params.odd_dims || {};
  const ODD_DIMS = {
    speed:    Array.isArray(_userOdd.speed)    ? _userOdd.speed    : ODD_DEFAULTS.speed,
    weather:  Array.isArray(_userOdd.weather)  ? _userOdd.weather  : ODD_DEFAULTS.weather,
    lighting: Array.isArray(_userOdd.lighting) ? _userOdd.lighting : ODD_DEFAULTS.lighting,
    road:     Array.isArray(_userOdd.road)     ? _userOdd.road     : ODD_DEFAULTS.road,
  };

  // 시스템 인자 (OEDR 성능 기반)
  const sys_factor = Math.min(1.0, oedr_perf / 0.88);

  // ── Speed × Weather 커버리지 매트릭스 ────────────────────────────────────
  const odd_speed_weather = ODD_DIMS.speed.map(sp => ({
    ...sp,
    cells: ODD_DIMS.weather.map(wt => {
      const cov     = +(sp.cov_base * wt.factor * sys_factor).toFixed(3);
      const risk_wt = +(sp.freq * wt.freq).toFixed(4);
      return { ...wt, cov, risk_wt, covered: cov >= 0.80 };
    }),
    avg_cov: +(ODD_DIMS.weather.reduce((s, wt) =>
      s + sp.cov_base * wt.factor * sys_factor, 0) / ODD_DIMS.weather.length).toFixed(3),
  }));

  // ── 3차원 ODD 커버리지 집계 (Speed × Weather × Lighting = 64 셀) ────────
  let covered_count = 0;
  const high_risk_uncovered = [];

  ODD_DIMS.speed.forEach(sp => {
    ODD_DIMS.weather.forEach(wt => {
      ODD_DIMS.lighting.forEach(lt => {
        const cov    = sp.cov_base * wt.factor * lt.factor * sys_factor;
        const riskW  = sp.freq * wt.freq * lt.freq;
        if (cov >= 0.80) {
          covered_count++;
        } else if (riskW > 0.003) {
          high_risk_uncovered.push({
            scenario:    `${sp.label} / ${wt.label} / ${lt.label}`,
            cov:         +cov.toFixed(3),
            risk_weight: +riskW.toFixed(5),
            gap:         +(0.80 - cov).toFixed(3),
          });
        }
      });
    });
  });
  high_risk_uncovered.sort((a, b) => b.risk_weight - a.risk_weight);
  const total_odd   = ODD_DIMS.speed.length * ODD_DIMS.weather.length * ODD_DIMS.lighting.length;
  const coverage_pct = +(covered_count / total_odd * 100).toFixed(1);

  // ── ODD 차원별 평균 커버리지 ─────────────────────────────────────────────
  const dim_summary = {
    speed:    ODD_DIMS.speed.map(sp => ({
      label: sp.label,
      avg_cov: +(sp.cov_base * sys_factor).toFixed(3),
    })),
    weather:  ODD_DIMS.weather.map(wt => ({
      label: wt.label,
      avg_cov: +(0.80 * wt.factor * sys_factor).toFixed(3),  // avg speed basis
    })),
    lighting: ODD_DIMS.lighting.map(lt => ({
      label: lt.label,
      avg_cov: +(0.80 * lt.factor * sys_factor).toFixed(3),
    })),
    road:     ODD_DIMS.road.map(rd => ({
      label: rd.label,
      avg_cov: +(0.80 * rd.factor * sys_factor).toFixed(3),
    })),
  };

  // ── Known Scenarios ([RevB] 8→16종 확장, ISO 21448 §6) ───────────────────
  const known_scenarios = [
    { id:"KS-01", scenario:"직진 추돌 — 전방 정지 차량",        scenarioEn:"Rear-end — Stationary Vehicle Ahead",        trigger:"고속 추월 후 감속",     triggerEn:"High-speed overtake then decel",       odd_speed:'highway',  odd_weather:'clear',  oedr:oedr_perf*0.95, residual_risk:"LOW"    },
    { id:"KS-02", scenario:"측방 끼어들기 — 사각지대",           scenarioEn:"Lane Cut-in — Blind Spot Vehicle",           trigger:"차선 변경 간섭",        triggerEn:"Lane change interference",             odd_speed:'suburban', odd_weather:'clear',  oedr:oedr_perf*0.82, residual_risk:"MEDIUM" },
    { id:"KS-03", scenario:"교차로 — 우선순위 오판",              scenarioEn:"Intersection — Priority Misjudgment",       trigger:"교통 신호 인식 오류",  triggerEn:"Traffic signal recognition error",     odd_speed:'city',     odd_weather:'clear',  oedr:oedr_perf*0.70, residual_risk:"HIGH"   },
    { id:"KS-04", scenario:"보행자 — 야간/역광 검출 한계",        scenarioEn:"Pedestrian — Night/Backlight Detect Limit",  trigger:"조명 불량 환경",        triggerEn:"Poor lighting environment",            odd_speed:'city',     odd_weather:'clear',  oedr:oedr_perf*0.60, residual_risk:"HIGH"   },
    { id:"KS-05", scenario:"강우/결빙 — 레이더 클러터",           scenarioEn:"Rain/Ice — Radar Clutter",                  trigger:"악천후 노면 반사",      triggerEn:"Adverse weather surface reflection",   odd_speed:'suburban', odd_weather:'rain',   oedr:oedr_perf*weather_factor*0.9, residual_risk:weather==="snow"?"HIGH":"MEDIUM" },
    { id:"KS-06", scenario:"터널/지하 — GPS 신호 손실",           scenarioEn:"Tunnel/Underground — GPS Signal Loss",      trigger:"GNSS 기반 기능 의존",  triggerEn:"GNSS-dependent functionality",         odd_speed:'highway',  odd_weather:'clear',  oedr:oedr_perf*0.85, residual_risk:"LOW"    },
    { id:"KS-07", scenario:"야간 강우 — 카메라+레이더 복합 열화", scenarioEn:"Night Rain — Camera+Radar Combined Degrad.", trigger:"야간+우천 동시",        triggerEn:"Night + rain simultaneous",            odd_speed:'city',     odd_weather:'rain',   oedr:oedr_perf*weather_factor*0.72*0.80, residual_risk:"HIGH" },
    { id:"KS-08", scenario:"고속 안개 — 카메라 무효·레이더 단독", scenarioEn:"High-speed Fog — Camera Null, Radar-only",  trigger:"카메라 완전 열화",      triggerEn:"Camera invalidated by dense fog",      odd_speed:'high_spd', odd_weather:'fog',    oedr:oedr_perf*0.42*0.65, residual_risk:"HIGH" },
    // ── [RevB] KS-09~KS-16 추가 ────────────────────────────────────────────
    { id:"KS-09", scenario:"자전거 — 교차로 횡단 가속",           scenarioEn:"Cyclist — Intersection Crossing Accel",     trigger:"저속 자전거 급출발",    triggerEn:"Low-speed cyclist sudden start",       odd_speed:'city',     odd_weather:'clear',  oedr:oedr_perf*0.65, residual_risk:"HIGH"   },
    { id:"KS-10", scenario:"공사구간 — 임시 차선 유도선 변경",    scenarioEn:"Construction Zone — Temp Lane Marking",     trigger:"표준 차선 위반 마킹",   triggerEn:"Non-standard temporary markings",      odd_speed:'suburban', odd_weather:'clear',  oedr:oedr_perf*0.72, residual_risk:"MEDIUM" },
    { id:"KS-11", scenario:"역주행 차량 — 고속도로 진입",         scenarioEn:"Wrong-Way Vehicle — Motorway Entry",        trigger:"예외적 역방향 주행",    triggerEn:"Reverse-direction travel",             odd_speed:'highway',  odd_weather:'clear',  oedr:oedr_perf*0.55, residual_risk:"HIGH"   },
    { id:"KS-12", scenario:"야간 동물 — 도로 횡단",               scenarioEn:"Night Animal — Road Crossing",              trigger:"비표준 객체 분류 실패", triggerEn:"Non-standard object classification",  odd_speed:'suburban', odd_weather:'clear',  oedr:oedr_perf*0.50, residual_risk:"HIGH"   },
    { id:"KS-13", scenario:"도로 이물질 — 저대비 물체 검출 한계", scenarioEn:"Road Debris — Low-Contrast Object Detect",  trigger:"표면 색상 동화",        triggerEn:"Surface color camouflage",             odd_speed:'highway',  odd_weather:'clear',  oedr:oedr_perf*0.68, residual_risk:"MEDIUM" },
    { id:"KS-14", scenario:"고속 합류 — 짧은 가속 차선",          scenarioEn:"High-Speed Merge — Short Acceleration Lane",trigger:"고속 상대 속도 계산",   triggerEn:"High relative speed estimation",       odd_speed:'highway',  odd_weather:'clear',  oedr:oedr_perf*0.78, residual_risk:"MEDIUM" },
    { id:"KS-15", scenario:"강설 — 차선 마킹 완전 불인식",        scenarioEn:"Heavy Snow — Complete Lane Marking Loss",   trigger:"노면 마킹 적설 피복",   triggerEn:"Snow-covered road markings",           odd_speed:'suburban', odd_weather:'snow',   oedr:oedr_perf*weather_factor*0.45, residual_risk:"HIGH" },
    { id:"KS-16", scenario:"터널 출구 — 순응 전 눈부심",          scenarioEn:"Tunnel Exit — Pre-Adaptation Glare",        trigger:"조도 급변 적응 지연",   triggerEn:"Rapid illumination change adaptation", odd_speed:'highway',  odd_weather:'clear',  oedr:oedr_perf*0.62, residual_risk:"MEDIUM" },
  ];

  const ks_pass      = known_scenarios.filter(s => s.oedr >= 0.99).length;
  const ks_all_pass  = ks_pass === known_scenarios.length; // [Fix-6] 전체 KS 통과 여부
  const ks_90_pass   = known_scenarios.filter(s => s.oedr >= 0.90).length;
  const ks_fail      = known_scenarios.filter(s => s.oedr < 0.90).length;
  const performance_gap = known_scenarios.reduce((s, k) => s + Math.max(0, 0.99 - k.oedr), 0);

  // ── Unknown Risk: 구 통계 모델 50% + ODD 미커버 비율 50% 블렌딩 ──────────
  const unknown_risk_stat = Math.max(0, (1 - oedr_perf) * 35 + 5);
  const unknown_risk_odd  = 100 - coverage_pct;
  const unknown_risk_pct  = +((unknown_risk_stat * 0.5 + unknown_risk_odd * 0.5)).toFixed(1);

  // SOTIF Score: ODD 커버리지 반영 (±0.3 가중)
  const sotif_score = Math.max(0, Math.min(100,
    Math.round(oedr_perf * 100 - performance_gap * 8 + (coverage_pct - 70) * 0.3)
  ));

  return {
    oedr_perf, weather_factor, speed_factor, temp_factor,
    // [RevE-Fix-SOTIF] certification_criteria_detail — 표준 출처 명시
    // ISO 21448:2022 조항 기반 기준과 사내 기준(Internal Criterion)을 명확히 구분
    // → 감사 시 각 합격 기준의 규범적 근거 제시 가능
    certification_criteria_detail: {
      oedr_threshold:     { value:0.99, type:'standard',  source:'ISO 21448:2022 §7.4.3 — OEDR 성능 한계 평가 기준. Known Scenario 전체 통과 요건.' },
      sotif_score_threshold:{ value:80, type:'internal',  source:'사내 기준 IC-SOTIF-01 (ISO 21448:2022 §6.4 기반 사내 수용 기준). 표준 직접 조항 없음 — V&V 계획서에 근거 별도 작성 필요.' },
      odd_coverage_threshold:{ value:85, type:'standard', source:'ISO 21448:2022 §5.4 — ODD 정의 및 커버리지 요건. 운용 설계 도메인 명시 의무.' },
      unknown_risk_threshold:{ value:10, type:'internal', source:'사내 기준 IC-SOTIF-02 (통계적 불확실성 관리 기준). ISO 21448:2022 §8.3 Unknown Scenario 처리 지침 기반.' },
    },
    known_scenarios,
    unknown_risk_pct, unknown_risk_stat: +unknown_risk_stat.toFixed(1), unknown_risk_odd: +unknown_risk_odd.toFixed(1),
    ks_pass, ks_all_pass, ks_90_pass, ks_fail,  // [Fix-6] ks_all_pass 추가; ks_pass는 보조 지표 유지
    performance_gap: +performance_gap.toFixed(3),
    sotif_score,
    // [Fix-6] 공식 인증 판정: ISO 21448 §6.4 — KS 전체 통과(OEDR≥0.99) AND SOTIF score≥80
    sotif_pass: ks_all_pass && sotif_score >= 80,
    certification_criterion:    'ISO 21448: KS 전체 통과(OEDR≥0.99) AND SOTIF score≥80',
    certification_criterion_en: 'ISO 21448: KS all-pass (OEDR≥0.99) AND SOTIF score≥80',
    certification_status:    sotif_score >= 90 && ks_all_pass ? "인증 가능" : sotif_score >= 70 ? "조건부 검토" : "재설계 필요",
    certification_status_en: sotif_score >= 90 && ks_all_pass ? "Certifiable" : sotif_score >= 70 ? "Conditional Review" : "Redesign Required",
    // [V42] ODD Coverage
    odd_dims: ODD_DIMS,
    odd_speed_weather,
    dim_summary,
    odd_coverage: dim_summary,   // [Sprint-3] alias for user-facing ODD override tests
    coverage_pct,
    covered_count,
    total_odd,
    high_risk_uncovered: high_risk_uncovered.slice(0, 8),
    odd_coverage_grade: coverage_pct >= 85 ? "✅ Adequate" : coverage_pct >= 70 ? "⚠️ Partial" : "❌ Insufficient",
  };
}

// ── computeSME ──
/**
 * 안전 메커니즘 효율성(SME, Safety Mechanism Effectiveness)을 계산한다.
 *
 * 10개 안전 메커니즘(SM-01~SM-10)별로 진단 커버리지(DC), 오탐률(false_pos),
 * 검출 지연(latency_ms)을 기반으로 SME를 산출한다:
 *   SME_i = DC × (1 - false_pos) × min(1, 10/latency_ms)
 *
 * 전체 SME는 FMEA 결과의 심각도(severity)로 가중 평균한다.
 * PMHF(Probabilistic Metric for Hardware Failures) = Σ λ_i × (1 - DC_i)
 * ASIL D 기준: PMHF < 1×10⁻⁸ /h
 *
 * @param {object[]} fmea_results  computeFMEA()의 반환값
 * @param {object}   params        차량 파라미터 (현재 미사용, 확장 예약)
 * @returns {{ mechanisms, cat_sme, overall_sme, pmhf, pmhf_pass, coverage_avg, grade } | null}
 */
function computeSME(fmea_results, params) {
  if (!fmea_results || fmea_results.length === 0) return null;

  // 안전 메커니즘 목록
  const mechanisms = [
    { id:"SM-01", name:"이중 APS 교차검증",       nameEn:"Dual APS Cross-check",     target_cat:"센서",      DC:0.97, false_pos:0.002, latency_ms:5   },
    { id:"SM-02", name:"CAN 타임아웃 감시",        nameEn:"CAN Timeout Monitor",      target_cat:"통신",      DC:0.95, false_pos:0.005, latency_ms:100 },
    { id:"SM-03", name:"배터리 전압 이중 감시",    nameEn:"Dual Battery Voltage Monitor", target_cat:"BMS",  DC:0.98, false_pos:0.001, latency_ms:10  },
    { id:"SM-04", name:"모터 토크 모니터",         nameEn:"Motor Torque Monitor",     target_cat:"모터",      DC:0.96, false_pos:0.003, latency_ms:20  },
    { id:"SM-05", name:"열폭주 감지 알고리즘",     nameEn:"Thermal Runaway Detection",target_cat:"열관리",    DC:0.92, false_pos:0.008, latency_ms:500 },
    { id:"SM-06", name:"회생제동 토크 감시",       nameEn:"Regen Brake Torque Monitor",target_cat:"제동",     DC:0.95, false_pos:0.004, latency_ms:50  },
    { id:"SM-07", name:"ESC 모니터",               nameEn:"ESC Monitor",               target_cat:"제어",     DC:0.99, false_pos:0.001, latency_ms:10  },
    { id:"SM-08", name:"절연 감시 IMD",            nameEn:"Insulation Monitor IMD",    target_cat:"전력전자", DC:0.99, false_pos:0.001, latency_ms:500 },
    { id:"SM-09", name:"SOC 이중 추정(KF+AH)",     nameEn:"Dual SOC Estimation (KF+AH)",target_cat:"BMS",    DC:0.90, false_pos:0.010, latency_ms:200 },
    { id:"SM-10", name:"냉각 유량 감시",           nameEn:"Coolant Flow Monitor",      target_cat:"열관리",   DC:0.88, false_pos:0.015, latency_ms:1000},
  ];

  // 카테고리별 SME 계산
  const cat_sme = {};
  mechanisms.forEach(sm => {
    const related = fmea_results.filter(f => f.category === sm.target_cat);
    if (related.length === 0) return;
    const avg_sev   = related.reduce((s, f) => s + f.severity, 0) / related.length;
    const avg_occ   = related.reduce((s, f) => s + f.occurrence, 0) / related.length;
    const sme_i     = sm.DC * (1 - sm.false_pos) * Math.min(1, 10 / Math.max(1, sm.latency_ms));
    cat_sme[sm.target_cat] = {
      mechanism: sm.name,
      mechanismEn: sm.nameEn || sm.name,
      DC: sm.DC,
      SME: +sme_i.toFixed(4),
      SME_pct: +(sme_i * 100).toFixed(1),
      latency_ms: sm.latency_ms,
      false_pos_rate: sm.false_pos,
      weighted_contribution: +(sme_i * avg_sev * avg_occ / 100).toFixed(3),
    };
  });

  // 전체 가중 SME
  const total_severity = fmea_results.reduce((s, f) => s + f.severity, 0);
  const weighted_sme = fmea_results.reduce((sum, f) => {
    const sm_cat = cat_sme[f.category];
    const sme_val = sm_cat ? sm_cat.SME : 0.5;
    return sum + sme_val * f.severity;
  }, 0);
  const overall_sme = total_severity > 0 ? +(weighted_sme / total_severity).toFixed(4) : 0;

  // PMHF 산출 (Probabilistic Metric for Hardware Failures)
  const pmhf = fmea_results.reduce((s, f) => {
    const sm_cat = cat_sme[f.category];
    const dc = sm_cat ? sm_cat.DC : 0.9;
    return s + f.lambda_h * (1 - dc);
  }, 0);

  return {
    mechanisms,
    cat_sme,
    overall_sme,
    overall_sme_pct: +(overall_sme * 100).toFixed(1),
    pmhf: +pmhf.toExponential(3),
    pmhf_pass: pmhf < 1e-8,  // ASIL D 기준: PMHF < 1×10⁻⁸/h
    coverage_avg: +(mechanisms.reduce((s, m) => s + m.DC, 0) / mechanisms.length).toFixed(3),
    grade: overall_sme >= 0.90 ? "Excellent" : overall_sme >= 0.75 ? "Good" : overall_sme >= 0.6 ? "Acceptable" : "Needs Improvement",
  };
}

// ── computeDFMEA ──
/**
 * 설계 FMEA(DFMEA, Design FMEA) 항목을 평가하여 RPN과 조치 우선순위를 계산한다.
 *
 * DFMEA는 설계 단계에서의 잠재 고장 모드를 분석하며 (AIAG-VDA FMEA 2019 §5),
 * 각 항목의 S(심각도) × O(발생빈도) × D(검출도) = RPN으로 위험을 정량화한다.
 * RPN ≥ 200: 즉시 설계 변경(HIGH), ≥ 100: 설계 검토(MEDIUM), < 100: 모니터링(LOW)
 *
 * @param {Array<{S, O, D, ...}>} dfmeaItems  DFMEA 항목 배열 (S/O/D 각 1~10 범위)
 * @returns {Array<{ RPN, RPN_pct, risk_level, action, actionEn, ...}>}
 */
function computeDFMEA(dfmeaItems) {
  return dfmeaItems.map(item => {
    const RPN = item.S * item.O * item.D;
    const risk_level = RPN >= 200 ? "HIGH" : RPN >= 80 ? "MEDIUM" : "LOW";
    const action = RPN >= 200 ? "즉시 설계 변경" : RPN >= 100 ? "설계 검토 필요" : "모니터링";
    const actionEn = RPN >= 200 ? "Immediate Design Change" : RPN >= 100 ? "Design Review Required" : "Monitoring";
    return {
      ...item,
      RPN,
      RPN_pct: +(RPN / 1000 * 100).toFixed(1),
      risk_level,
      action,
      actionEn,
    };
  });
}

// ── computePFMEA ──
/**
 * 공정 FMEA(PFMEA, Process FMEA) 항목을 평가하여 RPN과 조치 우선순위를 계산한다.
 *
 * PFMEA는 제조·조립 공정 단계에서의 잠재 고장 모드를 분석하며 (AIAG-VDA FMEA 2019 §6),
 * 각 항목의 S(심각도) × O(발생빈도) × D(검출도) = RPN으로 공정 위험을 정량화한다.
 * RPN ≥ 200: 공정 즉시 개선(HIGH), ≥ 100: 관리 계획 강화(MEDIUM), < 100: 모니터링(LOW)
 *
 * @param {Array<{S, O, D, process_step, ...}>} pfmeaItems  PFMEA 항목 배열 (S/O/D 각 1~10)
 * @returns {Array<{ RPN, RPN_pct, risk_level, action, actionEn, ... }>}
 */
function computePFMEA(pfmeaItems) {
  return pfmeaItems.map(item => {
    const RPN = item.S * item.O * item.D;
    const risk_level = RPN >= 200 ? "HIGH" : RPN >= 80 ? "MEDIUM" : "LOW";
    const action = RPN >= 200 ? "공정 즉시 개선" : RPN >= 100 ? "관리 계획 강화" : "모니터링";
    const actionEn = RPN >= 200 ? "Immediate Process Improvement" : RPN >= 100 ? "Strengthen Control Plan" : "Monitoring";
    return {
      ...item,
      RPN,
      RPN_pct: +(RPN / 1000 * 100).toFixed(1),
      risk_level,
      action,
      actionEn,
    };
  });
}

// ── linkDFMEA_PFMEA ──
/**
 * DFMEA 설계 고장과 PFMEA 공정 원인을 연계하여 종합적 위험 체인을 구성한다.
 *
 * dfmeaResults의 각 항목이 가진 process_cause_ids 배열을 기반으로
 * 해당 PFMEA 항목들을 조회하여 다음을 계산한다:
 *   - 공정 기여도: 연계된 PFMEA RPN 평균 / DFMEA RPN × 100 (%)
 *   - 설계 개선 여력: RPN - S × O × 1 (검출도 D를 1로 개선할 때의 RPN 감소량)
 *   - 종합 위험도: DFMEA RPN × 0.6 + PFMEA RPN 평균 × 0.4
 *   - 역방향: 각 PFMEA 항목이 몇 개의 DFMEA에 기여하는지 집계
 *
 * @param {object[]} dfmeaResults  computeDFMEA()의 반환값 (process_cause_ids 필드 포함)
 * @param {object[]} pfmeaResults  computePFMEA()의 반환값
 * @returns {{ linked: object[], pfmea_impact: object[], stats: object }}
 */
function linkDFMEA_PFMEA(dfmeaResults, pfmeaResults) {
  const pfmeaMap = {};
  pfmeaResults.forEach(p => { pfmeaMap[p.id] = p; });

  const linked = dfmeaResults.map(df => {
    const process_causes = (df.process_cause_ids || [])
      .map(pid => pfmeaMap[pid])
      .filter(Boolean);

    // 공정 기여도: 연계 PFMEA RPN 평균 / DFMEA RPN
    const process_rpn_avg = process_causes.length > 0
      ? process_causes.reduce((s, p) => s + p.RPN, 0) / process_causes.length
      : 0;
    const process_contribution_pct = df.RPN > 0
      ? Math.min(100, +(process_rpn_avg / df.RPN * 100).toFixed(1))
      : 0;

    // 설계 개선 여력: 현재 RPN - (D를 1로 낮췄을 때 RPN)
    const design_improvement_potential = df.RPN - df.S * df.O * 1;

    // 위험 전달 경로: 설계 고장 → 공정 원인 → 최종 영향
    const risk_chain = process_causes.map(p => `${p.id}(${p.process_step})`).join(" → ");

    return {
      ...df,
      process_causes,
      process_rpn_avg: +process_rpn_avg.toFixed(1),
      process_contribution_pct,
      design_improvement_potential,
      risk_chain: risk_chain || "직접 설계 결함",
      combined_criticality: +(df.RPN * 0.6 + process_rpn_avg * 0.4).toFixed(0),
    };
  });

  // 역방향: 각 PFMEA가 몇 개 DFMEA에 기여하는지
  const pfmea_impact = {};
  pfmeaResults.forEach(p => { pfmea_impact[p.id] = { ...p, linked_dfmea_count: 0, linked_dfmea_ids: [] }; });
  dfmeaResults.forEach(df => {
    (df.process_cause_ids || []).forEach(pid => {
      if (pfmea_impact[pid]) {
        pfmea_impact[pid].linked_dfmea_count++;
        pfmea_impact[pid].linked_dfmea_ids.push(df.id);
      }
    });
  });

  // 연계 통계
  const total_links = dfmeaResults.reduce((s, df) => s + (df.process_cause_ids || []).length, 0);
  const high_risk_linked = linked.filter(l => l.combined_criticality >= 200).length;

  return {
    linked,
    pfmea_impact: Object.values(pfmea_impact),
    stats: {
      total_dfmea: dfmeaResults.length,
      total_pfmea: pfmeaResults.length,
      total_links,
      avg_links_per_dfmea: +(total_links / dfmeaResults.length).toFixed(1),
      high_risk_linked,
      max_combined: Math.max(...linked.map(l => l.combined_criticality)),
      coverage_pct: +(linked.filter(l => l.process_causes.length > 0).length / linked.length * 100).toFixed(1),
    },
  };
}

// ── autoMapDFMEA_PFMEA ──
/**
 * NLP 기반 키워드 매칭으로 DFMEA 항목과 PFMEA 항목을 자동 연결한다.
 *
 * SYS_PROC_HINTS 키워드 사전을 활용하여 DFMEA의 서브시스템·컴포넌트 토큰과
 * PFMEA의 공정 단계·기능 토큰 간 교집합 점수를 계산한다:
 *   score += 1.0  직접 토큰 매칭
 *   score += 1.5  서브시스템 힌트 보너스
 *   score += 2.0  컴포넌트 직접 매칭 보너스 (가장 강한 신호)
 *
 * score ≥ 2인 후보를 상위 4개 제안(suggestions)으로 반환하며,
 * 수동 연결(manual_confirmed)과 자동 제안(auto_suggested)을 구분한다.
 * confidence = min(1.0, score / 12)
 *
 * @param {object[]} dfmeaItems  DFMEA 항목 배열
 * @param {object[]} pfmeaItems  PFMEA 항목 배열
 * @returns {{ results: object[], pfCoverage: object, stats: object }}
 */
function autoMapDFMEA_PFMEA(dfmeaItems, pfmeaItems) {
  // ── 공통 키워드 사전 (서브시스템 → 공정 단계 매핑 힌트) ──
  const SYS_PROC_HINTS = {
    '파워트레인': ['모터', '인버터', 'SW', '기어박스', '조립', '플래시'],
    'Powertrain': ['Motor', 'Inverter', 'SW', 'Gearbox', 'Assembly', 'Flash'],
    '배터리': ['BMS', '셀', '팩', '봉지', '밸런싱', '교정'],
    'Battery': ['BMS', 'Cell', 'Pack', 'Sealing', 'Balancing', 'Calibration'],
    '제동': ['ABS', '회생', '배관', '교정'],
    'Braking': ['ABS', 'Regen', 'Pipe', 'Calibration'],
    '조향': ['EPS', 'SAS', '기계'],
    'Steering': ['EPS', 'SAS', 'Mechanical'],
    '열': ['냉각', 'Cooling', '냉각판', 'Cooling Plate'],
    'Thermal': ['Cooling', 'Thermal'],
    '센서': ['PCB', 'CAN', '하네스'],
    'Sensor': ['PCB', 'CAN', 'Harness'],
    '안전': ['PCB', '인버터', '절연'],
    'Safety': ['PCB', 'Inverter', 'Insulation'],
    '제어': ['인버터', 'SW', '플래시'],
    'Control': ['Inverter', 'SW', 'Flash'],
  };

  // ── 토큰화 ──
  const tokenize = (str) => (str || '')
    .toLowerCase()
    .replace(/[()\/·\-]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 2);

  // ── PFMEA별 토큰 캐시 ──
  const pfTokenCache = pfmeaItems.map(pf => ({
    pf,
    tokens: [
      ...tokenize(pf.process_step), ...tokenize(pf.process_stepEn || ''),
      ...tokenize(pf.process_fn),   ...tokenize(pf.process_fnEn || ''),
      ...tokenize(pf.category),     ...tokenize(pf.categoryEn || ''),
    ],
  }));

  const results = dfmeaItems.map(df => {
    // DFMEA 쿼리 토큰
    const dfTokens = [
      ...tokenize(df.subsystem), ...tokenize(df.subsystemEn || ''),
      ...tokenize(df.component), ...tokenize(df.componentEn || ''),
      ...tokenize(df.failure_mode), ...tokenize(df.failure_modeEn || ''),
      ...tokenize(df.design_fn),   ...tokenize(df.design_fnEn || ''),
    ];
    // 서브시스템 힌트 토큰
    const hintTokens = Object.entries(SYS_PROC_HINTS)
      .filter(([k]) => (df.subsystem||'').includes(k) || (df.subsystemEn||'').includes(k))
      .flatMap(([,v]) => v.map(t => t.toLowerCase()));

    // 각 PFMEA 후보 점수 계산
    const scored = pfTokenCache.map(({ pf, tokens }) => {
      let score = 0;
      // 직접 토큰 교집합
      dfTokens.forEach(dt => {
        if (tokens.some(pt => pt.includes(dt) || dt.includes(pt))) score += 1;
      });
      // 힌트 보너스
      hintTokens.forEach(ht => {
        if (tokens.some(pt => pt.includes(ht) || ht.includes(pt))) score += 1.5;
      });
      // 컴포넌트 직접 매칭 보너스 (강한 신호)
      const compTokens = tokenize(df.component).concat(tokenize(df.componentEn || ''));
      compTokens.forEach(ct => {
        if (tokens.some(pt => pt.includes(ct) || ct.includes(pt))) score += 2;
      });
      const maxScore = 12;
      const confidence = Math.min(1.0, score / maxScore);
      return { pf, score, confidence };
    })
    .filter(s => s.score >= 2)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);  // 상위 4개 후보

    const manualIds  = new Set(df.process_cause_ids || []);
    const suggestions = scored.map(s => ({
      ...s,
      is_manual:  manualIds.has(s.pf.id),
      is_new:     !manualIds.has(s.pf.id),
      link_type:  manualIds.has(s.pf.id) ? 'manual_confirmed' : 'auto_suggested',
    }));
    const new_suggestions = suggestions.filter(s => s.is_new);

    return {
      ...df,
      suggestions,
      new_suggestions,
      auto_new_count: new_suggestions.length,
    };
  });

  // ── 역방향 공정 단계 커버리지 ──
  const pfCoverage = {};
  pfmeaItems.forEach(pf => { pfCoverage[pf.id] = { manual_count: 0, auto_count: 0, df_ids: [] }; });
  results.forEach(df => {
    (df.process_cause_ids || []).forEach(pid => {
      if (pfCoverage[pid]) { pfCoverage[pid].manual_count++; pfCoverage[pid].df_ids.push(df.id); }
    });
    df.new_suggestions.forEach(s => {
      if (pfCoverage[s.pf.id]) { pfCoverage[s.pf.id].auto_count++; }
    });
  });

  const total_new   = results.reduce((s, df) => s + df.auto_new_count, 0);
  const total_manual = results.reduce((s, df) => s + (df.process_cause_ids||[]).length, 0);

  return { results, pfCoverage, stats: { total_new, total_manual, total_dfmea: results.length } };
}

// ── generateControlPlan ──
/**
 * PFMEA와 DFMEA 연계 결과를 기반으로 관리 계획(Control Plan)을 생성한다.
 *
 * 각 PFMEA 공정 단계별로 연계된 DFMEA 설계 항목에서 최고 심각도를 도출하고
 * 특성 분류(CRITICAL/MAJOR/MINOR), 검사 빈도, SPC 기준, 검사 방법, 반응 계획을 결정한다:
 *
 * 특성 분류 기준:
 *   max_sev ≥ 9 → CRITICAL (100% 전수검사, 즉시 생산 중단)
 *   max_sev ≥ 7 → MAJOR    (AQL 1.0 샘플링, 불량품 격리)
 *   max_sev  < 7 → MINOR    (순찰 검사, 로그 기록)
 *
 * SPC 기준 (카테고리별):
 *   제조 → Cpk ≥ 1.67, 조립 → Ppk ≥ 1.33, 교정 → MSA(GR&R ≤ 10%), SW → SW Coverage ≥ 95%
 *
 * @param {object[]} pfmeaItems  PFMEA 항목 배열
 * @param {object[]} dfLinked    linkDFMEA_PFMEA().linked 배열
 * @returns {object[]}  관리 계획 항목 배열 (char_class, frequency, spc_type, method, reaction_plan 포함)
 */
function generateControlPlan(pfmeaItems, dfLinked) {
  // dfLinked: linkDFMEA_PFMEA().linked 배열
  const dfByPf = {};
  (dfLinked || []).forEach(df => {
    (df.process_cause_ids || []).forEach(pid => {
      if (!dfByPf[pid]) dfByPf[pid] = [];
      dfByPf[pid].push(df);
    });
  });

  const CATEGORY_SPC = {
    '제조': 'Cpk ≥ 1.67',      'Manufacturing': 'Cpk ≥ 1.67',
    '조립': 'Ppk ≥ 1.33',      'Assembly':      'Ppk ≥ 1.33',
    '교정': 'MSA (GR&R ≤ 10%)', 'Calibration':  'MSA (GR&R ≤ 10%)',
    '소프트웨어': 'SW Coverage ≥ 95%', 'Software': 'SW Coverage ≥ 95%',
  };
  const INSPECT_METHOD = {
    '제조': '자동 광학 검사(AOI) + 계측기', 'Manufacturing': 'AOI + CMM',
    '조립': '토크 렌치 기록 + 기능 시험',  'Assembly':      'Torque Record + Functional Test',
    '교정': '기준 교정기 비교 + 로그',     'Calibration':   'Reference Calibrator + Log',
    '소프트웨어': 'CRC / 서명 검증 + 히스토그램', 'Software': 'CRC/Signature + Histogram',
  };

  return pfmeaItems.map(pf => {
    const linked_df = dfByPf[pf.id] || [];
    const max_sev   = linked_df.length > 0 ? Math.max(...linked_df.map(df => df.S || df.severity || 0)) : pf.S;
    const char_class = max_sev >= 9 ? 'CRITICAL' : max_sev >= 7 ? 'MAJOR' : 'MINOR';
    const frequency  = max_sev >= 9 ? '100% 전수검사' : max_sev >= 7 ? '샘플링 (AQL 1.0)' : '순찰 (4시간/회)';
    const frequencyEn = max_sev >= 9 ? '100% Inspection' : max_sev >= 7 ? 'Sampling (AQL 1.0)' : 'Patrol (4h/cycle)';
    const spc_type   = CATEGORY_SPC[pf.category] || CATEGORY_SPC[pf.categoryEn] || 'Cpk ≥ 1.33';
    const method     = INSPECT_METHOD[pf.category] || INSPECT_METHOD[pf.categoryEn] || pf.control;
    const record_form = char_class === 'CRITICAL' ? 'QCP-CRIT-' + pf.id
                       : char_class === 'MAJOR'    ? 'QCP-MAJ-'  + pf.id
                       :                             'QCP-MIN-'  + pf.id;
    const reaction_plan = char_class === 'CRITICAL'
      ? '생산 즉시 중단 → 설계 검토'
      : char_class === 'MAJOR'
      ? '불량품 격리 → 공정 조정'
      : '공정 로그 기록';
    const reaction_planEn = char_class === 'CRITICAL'
      ? 'Stop Production → Design Review'
      : char_class === 'MAJOR'
      ? 'Quarantine → Process Adjustment'
      : 'Log & Monitor';

    return {
      ...pf,
      linked_df,
      max_sev,
      char_class,
      frequency,
      frequencyEn,
      spc_type,
      method,
      record_form,
      reaction_plan,
      reaction_planEn,
      linked_df_count: linked_df.length,
    };
  });
}

// ── CCF β 테이블 (ISO 26262-9 §5.4) ─────────────────────────────────────────
// AND 게이트 확률 계산 시 공통원인고장 기여분을 gate_prob에 명시적으로 합산
// CCF_BETA_FTA: ASIL 최고 레벨 기반 β 선택
const CCF_BETA_FTA = { D: 0.02, C: 0.05, B: 0.10, A: 0.15, QM: 0.20 };

/**
 * 자식 노드 배열 중 가장 높은 ASIL 등급을 반환한다 (QM < A < B < C < D).
 * CCF β 계수 선택에 사용.
 *
 * @param {{ asil?: string }[]} children  자식 노드 배열
 * @returns {'QM'|'A'|'B'|'C'|'D'}
 */
function _getHighestASIL(children) {
  const order = ["QM","A","B","C","D"];
  return children.reduce((best, c) => {
    const ca = c.asil || "QM";
    return order.indexOf(ca) > order.indexOf(best) ? ca : best;
  }, "QM");
}

// ── Boolean MCS 헬퍼 함수 (Fix-MCS-1) ──────────────────────────────────────
// 게이트 타입별 Boolean 전개:
//   OR      : 자식 컷셋 합집합
//   AND     : 자식 컷셋 Cartesian product (CCF는 확률에만 영향, 컷셋 구조는 동일)
//   INHIBIT : AND처럼 처리 (주 이벤트 + 조건 이벤트 모두 컷셋에 포함)
//   TRANSFER: 대상 TE 자식 컷셋을 OR로 평탄화
//   house   : 단일 원소 컷셋 [[id]] (조건 이벤트로 취급)
/**
 * FTA 노드에서 최소 컷셋(MCS) 집합을 Boolean 대수 재귀 전개로 계산한다.
 * OR: 자식 합집합, AND/INHIBIT: Cartesian product, TRANSFER: OR로 평탄화.
 * [Fix-MCS-1] IEC 61025 Boolean 대수 기반 구현.
 *
 * @param {object} node   FTA 트리 노드
 * @param {object} fmMap  고장모드 id → 객체 맵 (확률 참조용)
 * @returns {string[][]}  최소 컷셋 배열 (각 배열은 id 집합)
 */
function _computeMCSFromNode(node, fmMap) {
  if (!node) return [];
  // house 이벤트 / basic 이벤트 — 단일 원소 컷셋
  if (node.type === "basic" || node.type === "house") return node.id ? [[node.id]] : [];
  // TRANSFER 게이트 — 대상 하위 트리를 OR로 평탄화
  if (node.gate === "TRANSFER" || node.type === "transfer") {
    const childMCS = (node.children || []).map(c => _computeMCSFromNode(c, fmMap));
    return childMCS.flat();
  }
  const childMCS = (node.children || []).map(c => _computeMCSFromNode(c, fmMap));
  if (node.gate === "OR") return childMCS.flat();
  // AND / INHIBIT — Cartesian product
  return childMCS.reduce((acc, sets) => {
    const result = [];
    for (const a of acc) {
      for (const b of sets) {
        result.push([...new Set([...a, ...b])]);
      }
    }
    return result;
  }, [[]]); // 항등원: 빈 컷셋
}

/**
 * 컷셋 배열에서 상위 집합(superset)인 것을 제거하여 진정한 최소 컷셋만 반환한다.
 *
 * @param {string[][]} arr  컷셋 배열
 * @returns {string[][]}    최소화된 컷셋 배열
 */
function _minimizeMCS(arr) {
  // 다른 컷셋의 상위 집합인 것을 제거 (진정한 최소 컷셋)
  return arr.filter((a, i) =>
    !arr.some((b, j) => i !== j && b.length < a.length && b.every(e => a.includes(e)))
  );
}

/**
 * 컷셋 내 모든 사건이 독립적이라고 가정하여 컷셋 확률(AND 곱)을 계산한다.
 *
 * @param {string[]} set   컷셋 내 이벤트 id 배열
 * @param {object}   fmMap  id → { prob } 맵
 * @returns {number}  컷셋 확률 (사건 미발견 시 1e-9 기본값)
 */
function _probFromSet(set, fmMap) {
  // 컷셋 내 모든 사건이 독립 가정 → 곱(AND 확률)
  return set.reduce((p, id) => p * (fmMap[id]?.prob || 1e-9), 1);
}

// ── buildFaultTree ──
/**
 * 지정된 Top Event ID에 대한 결함 트리(Fault Tree)를 재귀적으로 구성한다.
 *
 * IEC 61025 및 ISO 26262-9를 기반으로 다음 게이트 타입을 지원한다:
 *
 *   OR 게이트:    P = 1 - Π(1 - P_i)  (희귀 고장 근사: ΣP_i)
 *   AND 게이트:   P = Π P_i + β × max(P_i)  [Add-CCF-AND: ISO 26262-9 §5.4 CCF 보정]
 *                 β: ASIL 최고 등급별 공통원인고장 인수 (D=0.02, C=0.05, B=0.10)
 *   INHIBIT 게이트: P = P_main × P_condition  [Add-INHIBIT: IEC 61025 §4.4.4]
 *                   main 이벤트(type≠house) × 조건 이벤트(type=house)
 *   TRANSFER 게이트: 다른 Top Event 하위 트리를 OR로 재사용  [Add-TRANSFER: IEC 61025 §4.4.5]
 *   House Event: 상시 발생/미발생 조건 이벤트  [Add-HOUSE: HOUSE_EVENTS 룩업]
 *
 * 최소 컷셋(MCS)은 Boolean 대수 전개 후 상위 집합을 제거하는 _minimizeMCS()로 계산한다.
 *
 * [Fix-IE05-struct] intermediate_events(IE)와 basic_events(FM) 분리 처리로 혼재 오류 수정.
 *
 * @param {string}   topEventId  분석할 Top Event ID (FTA_TOP_EVENTS에서 조회)
 * @param {object[]} faultModes  고장 모드 정의 배열 (id, prob, asil 포함)
 * @returns {{ topEvent, tree, mcs, gate_summary, top_prob, asil, ... } | null}
 *   트리 구조, 최소 컷셋, ASIL, 상위 컷셋 목록 포함. topEventId 미발견 시 null.
 */
// [Fix-DEPTH-1]  재귀 buildNode — N계층 IE 트리 지원
// [Fix-MCS-1]    Boolean 대수 기반 최소 컷셋
// [Add-CCF-AND]  AND 게이트: ISO 26262-9 §5.4 CCF β 보정 (P_ind + β×P_dom)
// [Add-INHIBIT]  INHIBIT 게이트: P = P_main × P_condition (IEC 61025 §4.4.4)
// [Add-HOUSE]    House Event: HOUSE_EVENTS 룩업 → type="house"
// [Add-TRANSFER] TRANSFER 게이트: 다른 TE 하위 트리 재사용 (IEC 61025 §4.4.5)
// [Add-TE-gate]  Top Event 게이트 te.gate 기반 top_prob 계산
function buildFaultTree(topEventId, faultModes) {
  const te = FTA_TOP_EVENTS.find(t => t.id === topEventId);
  if (!te) return null;

  const fmMap = {};
  faultModes.forEach(fm => { fmMap[fm.id] = fm; });

  function buildNode(nodeId, level) {
    // [Add-HOUSE] House Event 처리
    const he = HOUSE_EVENTS?.[nodeId];
    if (he) {
      return { id: nodeId, name: he.name, nameEn: he.nameEn,
               type: "house", prob: he.prob, level,
               description: he.description };
    }

    const ie = FTA_INTERMEDIATE_EVENTS[nodeId];
    if (ie) {
      // [Add-TRANSFER] Transfer 게이트: 다른 TE 하위 트리 전개
      if (ie.gate === "TRANSFER") {
        const target_te = FTA_TOP_EVENTS.find(t => t.id === ie.transfer_to);
        if (target_te) {
          const transferred = target_te.intermediate_causes.map(ic => buildNode(ic, level + 1));
          const t_prob = 1 - transferred.reduce((p, c) => p * (1 - (c.prob || 0)), 1);
          return { id: nodeId, name: ie.name, nameEn: ie.nameEn,
                   type: "transfer", gate: "TRANSFER",
                   transfer_to: ie.transfer_to, prob: t_prob,
                   children: transferred, level };
        }
      }

      // 재귀 전개: intermediate_events(하위 IE) + basic_events(FM) 분리 처리
      // [Fix-IE05-struct] intermediate_events 필드 도입에 따른 구조 대응
      //   기존: basic_events에 IE-xx와 FM-xx 혼재 → FM 분기 오류 가능성
      //   수정: intermediate_events(IE) 먼저 전개 후 basic_events(FM) 전개, 합산
      const ie_children  = (ie.intermediate_events || []).map(ieid => buildNode(ieid, level + 1));
      const fm_children  = (ie.basic_events || []).map(beid => buildNode(beid, level + 1));
      const children = [...ie_children, ...fm_children];

      let gate_prob, ccf_prob = 0, ccf_beta_used = 0;

      if (ie.gate === "AND") {
        // [Add-CCF-AND] ISO 26262-9 §5.4: P_total = P_independent + β × P_dominant
        const p_ind = children.reduce((p, c) => p * (c.prob || 1e-10), 1);
        const beta  = ie.ccf_beta ?? CCF_BETA_FTA[_getHighestASIL(children)] ?? 0.05;
        const p_dom = Math.max(...children.map(c => c.prob || 0));
        ccf_prob     = beta * p_dom;
        ccf_beta_used = beta;
        gate_prob    = p_ind + ccf_prob;

      } else if (ie.gate === "INHIBIT") {
        // [Add-INHIBIT] IEC 61025 §4.4.4: P = P_main_events × P_condition
        const main_nodes = children.filter(c => c.type !== "house");
        const cond_nodes = children.filter(c => c.type === "house");
        const p_main = main_nodes.length > 0
          ? 1 - main_nodes.reduce((p, c) => p * (1 - (c.prob || 0)), 1)
          : 0;
        const p_cond = cond_nodes.length > 0 ? (cond_nodes[0].prob ?? 1.0) : 1.0;
        gate_prob = p_main * p_cond;

      } else { // OR
        gate_prob = 1 - children.reduce((p, c) => p * (1 - (c.prob || 0)), 1);
      }

      return {
        id: nodeId, name: ie.name, nameEn: ie.nameEn,
        type: "intermediate", gate: ie.gate,
        prob: gate_prob, prob_base: ie.prob_base,
        ...(ccf_prob > 0 && { ccf_prob: +ccf_prob.toExponential(2), ccf_beta: ccf_beta_used }),
        children, level,
      };
    }

    // Basic event (FM)
    const fm = fmMap[nodeId];
    if (fm) {
      return { id:fm.id, name:fm.name, type:"basic", category:fm.category,
               prob:fm.prob, asil:fm.asil || calcASIL_safe(fm.S, fm.E, fm.C),
               S:fm.S, E:fm.E, C:fm.C, level };
    }
    return { id:nodeId, name:"(미정의)", type:"basic", prob:1e-9, level };
  }

  const intermediate_nodes = te.intermediate_causes.map(ic => buildNode(ic, 1));

  // [Add-TE-gate] Top Event 확률: te.gate 기반 계산 (기본 OR)
  let top_prob;
  if (te.gate === "AND") {
    const p_ind = intermediate_nodes.reduce((p, n) => p * (n.prob || 1e-10), 1);
    const beta  = CCF_BETA_FTA["C"]; // TE 레벨 CCF 보수적 적용
    const p_dom = Math.max(...intermediate_nodes.map(n => n.prob || 0));
    top_prob = p_ind + beta * p_dom;
  } else {
    top_prob = 1 - intermediate_nodes.reduce((p, n) => p * (1 - (n.prob || 0)), 1);
  }

  // [Fix-MCS-1] Boolean 대수 기반 최소 컷셋
  const rootNode = { type:"intermediate", gate:"OR", children: intermediate_nodes };
  const rawMCS   = _computeMCSFromNode(rootNode, fmMap);
  const mcs      = _minimizeMCS(rawMCS).map(set => {
    const prob = _probFromSet(set, fmMap);
    const maxP = Math.max(...set.map(id => fmMap[id]?.prob || 1e-9));
    return {
      set, prob,
      criticality: prob > 1e-7 ? "HIGH" : prob > 1e-8 ? "MEDIUM" : "LOW",
      order: set.length,
      dominant_event: set.find(id => (fmMap[id]?.prob || 0) === maxP),
    };
  }).sort((a, b) => b.prob - a.prob);

  const pfh_margin = te.target_pfh > 0 ? +(top_prob / te.target_pfh).toFixed(2) : null;
  const pfh_pass   = pfh_margin !== null && pfh_margin <= 1.0;

  return {
    top_event: { ...te, prob: top_prob },
    intermediate_nodes,
    mcs,
    stats: {
      total_basic_events: intermediate_nodes.reduce((s, n) => s + (n.children?.length || 0), 0),
      total_intermediate: intermediate_nodes.length,
      top_prob, pfh_margin, pfh_pass,
      target_pfh: te.target_pfh,
      dominant_mcs: mcs[0],
      order1_count: mcs.filter(m => m.order === 1).length,
      order2_count: mcs.filter(m => m.order === 2).length,
    },
  };
}

// ── computeFTA ──
/**
 * 모든 Top Event에 대한 결함 트리 분석(FTA, Fault Tree Analysis)을 일괄 수행한다.
 *
 * FTA_TOP_EVENTS에 정의된 각 Top Event별로 buildFaultTree()를 호출하여
 * 트리 구조, 최소 컷셋(MCS), Top Event 발생 확률, ASIL을 계산한다.
 *
 * @param {object[]} faultModes  고장 모드 정의 배열 (constants/FAULT_MODES.js)
 * @returns {object[]}  Top Event별 결함 트리 분석 결과 배열 (null 제외)
 */
function computeFTA(faultModes) {
  return FTA_TOP_EVENTS.map(te => buildFaultTree(te.id, faultModes)).filter(Boolean);
}

// ── analyzeHARA ──
/**
 * ISO 26262 Part 3에 따라 위험원 분석 및 위험성 평가(HARA, Hazard Analysis and Risk Assessment)를 수행한다.
 *
 * 각 고장 모드에 대해 S(심각도), E(노출도), C(제어 가능성)를 결정하고
 * ASIL 등급을 산출한다. 파라미터 연동:
 *   - 차량 속도 v > 130km/h → E를 최대 1 단계 증가 (고속 고노출)
 *   - 차량 속도 v < 60km/h  → E를 최대 1 단계 감소 (저속 저노출)
 *
 * haraOverrides[id]로 특정 고장 모드의 S/E/C를 사용자가 직접 편집할 수 있으며,
 * 오버라이드 적용 여부는 _overridden 플래그로 표시된다.
 *
 * @param {object} params         차량 파라미터 (hara_vehicle_speed_kmh: 대표 속도)
 * @param {object} [haraOverrides]  { [hazard_id]: { S?, E?, C? } } 사용자 편집값
 * @returns {object[]}  ASIL이 높은 순으로 정렬된 HARA 결과 배열
 *   각 항목: { id, name, S, C, E_eff, asil, asil_invalid, asil_score, risk_priority, _overridden }
 */
function analyzeHARA(params, haraOverrides = {}) {
  return FAULT_MODES.map(fm => {
    // ① 오버라이드 적용: haraOverrides[id]에 S/E/C 편집값이 있으면 우선 적용
    const ov = haraOverrides[fm.id] || {};
    const S = (ov.S != null) ? ov.S : fm.S;
    const C = (ov.C != null) ? ov.C : fm.C;

    // 파라미터 연동: E는 오버라이드가 없을 때만 차량 속도 보정 적용
    let E_eff = (ov.E != null) ? ov.E : fm.E;
    if (ov.E == null) {
      const v = params.hara_vehicle_speed_kmh || 120;
      if (v > 130) E_eff = Math.min(4, E_eff + 1);
      if (v < 60)  E_eff = Math.max(1, E_eff - 1);
    }

    const asil = calcASIL(S, E_eff, C);  // null = 범위 초과 입력 (오버라이드 값 검증 실패)
    const asil_safe = asil ?? "QM";       // UI 렌더링용 안전 폴백
    return {
      ...fm,
      S, C, E_eff,
      asil: asil_safe,
      asil_invalid: asil === null,        // 입력 범위 오류 플래그 — UI에서 경고 표시용
      asil_score: ASIL_SCORE[asil_safe] ?? 0,
      risk_priority: S * E_eff * C,
      _overridden: Object.keys(ov).length > 0,  // 편집 여부 표시용
    };
  }).sort((a, b) => b.asil_score - a.asil_score || b.risk_priority - a.risk_priority);
}

// ── generateFSR_TSR ──
/**
 * HARA 결과에서 기능 안전 요구사항(FSR)과 기술 안전 요구사항(TSR)을 생성한다.
 *
 * ASIL이 'QM'이 아닌 항목만 대상으로 하며, 각 안전 목표(SG)에 연계된
 * FSR(Functional Safety Requirement)과 TSR(Technical Safety Requirement)을
 * 추출하여 추적성 테이블을 구성한다.
 *
 * 출력 형식은 ISO 26262-4 §6(안전 목표) 및 §7(FSR/TSR) 문서 구조를 따른다.
 *
 * @param {object[]} haraResults  analyzeHARA()의 반환값 (asil !== 'QM'인 항목 필터링)
 * @returns {Array<{ id, hazard_id, asil, safety_goal, safety_goalEn, fsr, fsrEn, tsr, tsrEn, verification }>}
 */
function generateFSR_TSR(haraResults) {
  return haraResults
    .filter(h => h.asil !== "QM")
    .map((h, idx) => ({
      id: `SG-${String(idx+1).padStart(2,"0")}`,
      hazard_id: h.id,
      asil: h.asil,
      safety_goal:   h.safety_goal,
      safety_goalEn: h.safety_goalEn,
      fsr:   h.fsr,
      fsrEn: h.fsrEn,
      tsr:   h.tsr,
      tsrEn: h.tsrEn,
      // lang-neutral; rendered via lang check in table
      verification: `Sim: ${h.sim_metric} < ${h.threshold} ${h.unit}`,
    }));
}

// ── generateHARAReport ──
/**
 * HARA 분석 결과와 시뮬레이션 검증 결과를 CSV 보고서 문자열로 생성한다.
 *
 * 출력 구조:
 *   §1 헤더 (생성일, 차량 속도 기준)
 *   §2 HARA 테이블 (ID, 위험명, 카테고리, S/E/C, ASIL, 안전 목표, 검증 메트릭)
 *   §3 FSR/TSR 연계 테이블 (generateFSR_TSR 결과)
 *   §4 시뮬레이션 검증 결과 (mean, P95, P99, fail_rate, safety_margin, PASS/FAIL)
 *
 * @param {object[]} haraResults   analyzeHARA()의 반환값
 * @param {object}   simResults    { [hazard_id]: { mean, p95, p99, fail_rate, safety_margin, pass } }
 * @param {object}   params        차량 파라미터 (hara_vehicle_speed_kmh)
 * @returns {string}  쉼표 구분 CSV 형식 보고서 문자열
 */
function generateHARAReport(haraResults, simResults, params) {
  const lines = [
    ["ev-sim HARA 보고서", `생성: ${new Date().toISOString().slice(0,10)}`,"","","","","","",""],
    ["차량 속도 기준", params.hara_vehicle_speed_kmh||120, "km/h","","","","","",""],
    ["","","","","","","","",""],
    ["ID","위험 명칭","카테고리","S","E","C","ASIL","안전 목표","검증 메트릭"],
    ...haraResults.map(h=>[h.id,h.name,h.category,h.S,h.E_eff,h.C,h.asil,h.safety_goal,`${h.sim_metric}<${h.threshold}${h.unit}`]),
    ["","","","","","","","",""],
    ["FSR/TSR 연계 테이블","","","","","","","",""],
    ["ID","ASIL","안전 목표","FSR","TSR","","","",""],
    ...generateFSR_TSR(haraResults).map(r=>[r.id,r.asil,r.safety_goal,r.fsr,r.tsr,"","","",""]),
    ["","","","","","","","",""],
    ["시뮬레이션 검증 결과","","","","","","","",""],
    ["해저드 ID","평균값","P95 (상위 95%)","P99","실패율(%)","안전마진(σ)","판정","",""],
    ...Object.entries(simResults||{}).map(([id,sr])=>[id,sr.mean,sr.p95,sr.p99,sr.fail_rate,sr.safety_margin,sr.pass?"PASS":"FAIL","",""]),
  ];
  return lines.map(row=>row.map(c=>`"${c}"`).join(",")).join("\n");
}


// ══════════════════════════════════════════════════════════════════
// [GAP-5] ASIL 분해 (Decomposition) — ISO 26262-9 §5.4
// ASIL D→B+B, C→A+B, B→QM+B, A→QM+A
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// ASIL 분해 (Decomposition) — ISO 26262-9 §5.4
// [Fix-DECOMP-1] independent:true 플래그 → 구조화된 독립성 증거로 교체
//
// 문제: independent:true 선언은 감사자에게 증거로 인정되지 않음.
//       ISO 26262-9 §5.4.4는 "독립성에 대한 주장(Argument)과 근거(Evidence)"를
//       명시적으로 요구. 단순 플래그는 요건 미충족.
//
// 수정: 각 분해 요소(E1/E2)에 independence_criteria 배열 추가.
//   - criterion_id  : 식별자 (IC-xx)
//   - type          : 독립성 유형 (physical|power|ground|sw_partition|thermal|diverse_impl)
//   - requirement   : 표준 요건 기술
//   - requirementEn : 영문
//   - verification  : 검증 방법 (DV 시험, 설계 검토, 분석)
//   - iso_clause    : 참조 표준 조항
//   - status        : 이행 상태 (REQUIRED — 제품 설계에서 증거 생성 필요)
//
// 이 구조는 computeASILDecomposition() 결과물에 포함되며,
// 감사 패키지의 Independence Evidence 시트 기초 자료로 사용됩니다.
// ══════════════════════════════════════════════════════════════════

// 분해 유형별 공통 독립성 기준 라이브러리
const INDEPENDENCE_CRITERIA_LIB = {
  physical: {
    criterion_id: 'IC-01',
    type: 'physical',
    requirement: 'E1·E2 는 별도 PCB 또는 물리적으로 분리된 모듈에 구현. 동일 커넥터·케이블 묶음 금지 (최소 이격 거리 ≥20mm).',
    requirementEn: 'E1·E2 implemented on separate PCBs or physically isolated modules. Shared connectors/cable bundles prohibited (min. separation ≥20mm).',
    verification: 'HW 레이아웃 검토(DRC) + 기구 도면 이격 거리 측정 + DV 충격/진동 시험 후 분리 유지 확인',
    verificationEn: 'HW layout review (DRC) + mechanical drawing separation measurement + post-DV shock/vibration separation check',
    iso_clause: 'ISO 26262-9 §5.4.4 (a)',
    status: 'REQUIRED',
  },
  power: {
    criterion_id: 'IC-02',
    type: 'power',
    requirement: 'E1·E2 전원 공급 경로 독립. 공통 레귤레이터/퓨즈 금지. 각각 독립 LDO 또는 별도 DC-DC 채널에서 공급.',
    requirementEn: 'E1·E2 power supply paths independent. Shared regulator/fuse prohibited. Each supplied from independent LDO or separate DC-DC channel.',
    verification: '전원 설계 검토 + 단락 고장 주입: E1 전원 차단 시 E2 전압 정상 유지 확인 (DV 고장 주입 시험)',
    verificationEn: 'Power design review + fault injection: verify E2 voltage stable when E1 power cut (DV fault injection test)',
    iso_clause: 'ISO 26262-9 §5.4.4 (b)',
    status: 'REQUIRED',
  },
  ground: {
    criterion_id: 'IC-03',
    type: 'ground',
    requirement: 'E1·E2 기준 접지(Reference Ground) 분리 또는 스타 접지 구조 적용. 공통 임피던스 결합(Ground Loop)으로 인한 간섭 제거.',
    requirementEn: 'E1·E2 reference ground separated or star-ground topology applied. Common-impedance coupling (ground loop) interference eliminated.',
    verification: '접지 임피던스 측정 (<100mΩ 간 분리) + EMC 방사 시험에서 채널 간 크로스토크 한계 확인',
    verificationEn: 'Ground impedance measurement (<100mΩ separation) + EMC radiated test cross-talk limit verification between channels',
    iso_clause: 'ISO 26262-9 §5.4.4 (b)',
    status: 'REQUIRED',
  },
  sw_partition: {
    criterion_id: 'IC-04',
    type: 'sw_partition',
    requirement: 'E1·E2 SW는 OS 메모리 파티션(MPU)으로 분리. 파티션 간 직접 메모리 접근 금지. AUTOSAR OS Category 2 ISR 이상 사용.',
    requirementEn: 'E1·E2 SW separated by OS memory partition (MPU). Direct inter-partition memory access prohibited. AUTOSAR OS Category 2 ISR or higher.',
    verification: 'AUTOSAR OS 파티션 설정 검토 + SW 통합 시험에서 파티션 침범 감지(MPU Fault 주입)',
    verificationEn: 'AUTOSAR OS partition config review + SW integration test partition violation detection (MPU fault injection)',
    iso_clause: 'ISO 26262-9 §5.4.4 (c) + ISO 26262-6 §7.4.9',
    status: 'REQUIRED',
  },
  thermal: {
    criterion_id: 'IC-05',
    type: 'thermal',
    requirement: 'E1·E2 동일 열원(방열판, 냉각판)에 의한 동시 열 열화 방지. 운영 온도 범위 내 열 CCF 가능성 평가 및 차폐 처리.',
    requirementEn: 'Prevent simultaneous thermal degradation of E1·E2 from shared heat sources (heatsink, cooling plate). Assess thermal CCF within operating temp range and apply shielding.',
    verification: '열 시뮬레이션(FEA) + 열화상 촬영(DV 열 스트레스 시험) + ZONE DFA 열 항목 PASS 확인',
    verificationEn: 'Thermal simulation (FEA) + thermal imaging (DV thermal stress test) + ZONE DFA thermal item PASS confirmation',
    iso_clause: 'ISO 26262-9 §7 (DFA) + ISO 26262-9 §5.4.4',
    status: 'REQUIRED',
  },
  diverse_impl: {
    criterion_id: 'IC-06',
    type: 'diverse_impl',
    requirement: 'E1·E2 SW 설계 다양성(Design Diversity) 적용: 독립 개발팀, 독립 컴파일러/툴체인, 독립 알고리즘. 공통 원인 SW 결함 방지.',
    requirementEn: 'E1·E2 SW design diversity: independent development teams, independent compiler/toolchain, independent algorithms. Prevent common-cause SW defects.',
    verification: '개발 독립성 계획서 검토 + SW 아키텍처 검토 + 코드 비교 분석(동일 패턴 검출)',
    verificationEn: 'Development independence plan review + SW architecture review + code comparison analysis (identical pattern detection)',
    iso_clause: 'ISO 26262-9 §5.4.5 + ISO 26262-6 §5.4.8',
    status: 'REQUIRED',
  },
};

// 분해 ASIL 레벨별 요구 독립성 기준 매핑
// ASIL D 분해 → 더 엄격한 기준 (6종 전체)
// ASIL C/B 분해 → 최소 4종 (physical, power, ground, sw_partition)
const DECOMP_INDEPENDENCE_CRITERIA = {
  D: ['physical', 'power', 'ground', 'sw_partition', 'thermal', 'diverse_impl'],
  C: ['physical', 'power', 'ground', 'sw_partition'],
  B: ['physical', 'power', 'ground', 'sw_partition'],
  A: ['physical', 'power'],
};

const ASIL_DECOMP_TABLE = {
  D: [
    { asil: 'B', element_id: 'E1', role: '주 채널 (Primary Channel)',   roleEn: 'Primary Channel',   criteria_types: DECOMP_INDEPENDENCE_CRITERIA.D },
    { asil: 'B', element_id: 'E2', role: '감시 채널 (Monitor Channel)', roleEn: 'Monitor Channel',   criteria_types: DECOMP_INDEPENDENCE_CRITERIA.D },
  ],
  C: [
    { asil: 'A', element_id: 'E1', role: '주 채널 (Primary Channel)',   roleEn: 'Primary Channel',   criteria_types: DECOMP_INDEPENDENCE_CRITERIA.C },
    { asil: 'B', element_id: 'E2', role: '감시 채널 (Monitor Channel)', roleEn: 'Monitor Channel',   criteria_types: DECOMP_INDEPENDENCE_CRITERIA.C },
  ],
  B: [
    { asil: 'QM', element_id: 'E1', role: '기능 채널 (Functional)',     roleEn: 'Functional Channel', criteria_types: DECOMP_INDEPENDENCE_CRITERIA.B },
    { asil: 'B',  element_id: 'E2', role: '안전 채널 (Safety)',         roleEn: 'Safety Channel',     criteria_types: DECOMP_INDEPENDENCE_CRITERIA.B },
  ],
  A: [
    { asil: 'QM', element_id: 'E1', role: '기능 채널 (Functional)',     roleEn: 'Functional Channel', criteria_types: DECOMP_INDEPENDENCE_CRITERIA.A },
    { asil: 'A',  element_id: 'E2', role: '안전 채널 (Safety)',         roleEn: 'Safety Channel',     criteria_types: DECOMP_INDEPENDENCE_CRITERIA.A },
  ],
};
const PMHF_TARGET = { D:1e-8, C:1e-7, B:1e-7, A:1e-6, QM:null };

/**
 * ISO 26262-9 §5.4에 따라 ASIL 분해(Decomposition)를 수행하고 독립성 증거 구조를 생성한다.
 *
 * ASIL 분해 규칙 (ISO 26262-9 Table D.1):
 *   ASIL D → B(E1) + B(E2)   (CCF β=2%)
 *   ASIL C → A(E1) + B(E2)   (CCF β=5%)
 *   ASIL B → QM(E1) + B(E2)  (CCF β=10%)
 *   ASIL A → QM(E1) + A(E2)  (CCF β=15%)
 *
 * PMHF 예산은 원래 ASIL 목표를 E1/E2로 균등 배분한다.
 * 각 분해 요소에는 INDEPENDENCE_CRITERIA_LIB 기반 독립성 기준 목록(independence_criteria)이
 * 포함되어 감사 패키지의 "Independence Evidence" 시트 원본 데이터로 사용된다.
 *
 * [Fix-DECOMP-1] independent:true 단순 플래그 제거 → 구조화된 독립성 기준으로 교체.
 * ASIL D: 6종(physical, power, ground, sw_partition, thermal, diverse_impl) 전체 요구.
 * ASIL C/B: 4종(physical, power, ground, sw_partition) 최소 요구.
 *
 * @param {object[]} haraResults  analyzeHARA()의 반환값 (asil !== 'QM'인 항목 대상)
 * @returns {object[]}  위험원별 ASIL 분해 결과 배열
 *   각 항목: { hazard_id, original_asil, decomposition[], independence_summary, rationale, ... }
 */
// [Fix-DECOMP-1] computeASILDecomposition — 독립성 증거 구조 완전 생성
// independent:true 플래그 제거 → INDEPENDENCE_CRITERIA_LIB 기반 기준 목록 생성
// 출력물은 감사 패키지의 "Independence Evidence" 시트 원본 데이터로 사용
function computeASILDecomposition(haraResults) {
  // ASIL별 CCF β — ISO 26262-9 §7.4.2 Table B.1
  const CCF_BETA = { D: 0.02, C: 0.05, B: 0.10, A: 0.15 };

  // HW/SW 요건 매핑
  const HW_REQ = { D: 'ASIL D HW (1oo2D 또는 동등)', B: 'ASIL B HW', A: 'ASIL A HW', QM: 'QM' };
  const SW_REQ = { D: 'SIL 3 (ASIL D)', B: 'SIL 1 (ASIL B)', A: 'SIL 1 (ASIL A)', QM: 'QM SW' };

  return haraResults
    .filter(h => h.asil && h.asil !== 'QM')
    .map(h => {
      const parts      = ASIL_DECOMP_TABLE[h.asil] || [];
      const ccf_factor = CCF_BETA[h.asil] ?? 0.10;
      const pmhf_total = PMHF_TARGET[h.asil];
      const ccfPct     = (ccf_factor * 100).toFixed(0);

      // 분해 요소별 상세 생성
      const decomposition = parts.map(p => {
        // 이 요소에 적용할 독립성 기준 목록을 라이브러리에서 조합
        const independence_criteria = (p.criteria_types || []).map(ctype => ({
          ...INDEPENDENCE_CRITERIA_LIB[ctype],
          element_pair: `${h.id}-E1 ↔ ${h.id}-E2`,  // 기준이 적용되는 요소 쌍
        }));

        return {
          element_id:  `${h.id}-${p.element_id}`,
          element_tag: p.element_id,           // 'E1' | 'E2'
          asil:        p.asil,
          role:        p.role,
          roleEn:      p.roleEn,
          pmhf_target: pmhf_total
            ? (pmhf_total / parts.length).toExponential(1)
            : '—',
          design_req:  HW_REQ[p.asil] || 'QM',
          sw_req:      SW_REQ[p.asil] || 'QM SW',
          // ── 핵심: 구조화된 독립성 기준 (감사 증거 원본) ──────────────
          independence_criteria,
          criteria_count:      independence_criteria.length,
          // 상태 요약: 모든 기준이 REQUIRED 상태 = 현장 설계에서 증거 생성 필요
          independence_status: independence_criteria.every(c => c.status === 'REQUIRED')
            ? 'PENDING_EVIDENCE'   // 기준 정의됨, 실물 증거 미생성
            : 'PARTIAL',
        };
      });

      const decompStr = decomposition.map(p => p.asil).join('+');

      // 독립성 요약 — 분해 전체 기준 개수 및 충족 판정 근거
      const all_criteria_types = [...new Set(
        decomposition.flatMap(p => (p.independence_criteria || []).map(c => c.type))
      )];
      const independence_summary = {
        total_criteria:   all_criteria_types.length,
        criteria_types:   all_criteria_types,
        // 인증 기준: ASIL D 분해는 6종 전체, C/B는 4종 이상 필요
        required_count:   h.asil === 'D' ? 6 : h.asil === 'C' ? 4 : 2,
        status:           'PENDING_EVIDENCE',
        note:             'independence_criteria 각 항목의 verification 절차를 DV/PV 시험 계획에 포함하고, 결과를 증거 패키지에 기록해야 합니다.',
        noteEn:           'Each independence_criteria item\'s verification procedure must be included in DV/PV test plans, and results recorded in the evidence package.',
        iso_ref:          'ISO 26262-9 §5.4.4 — Freedom from Interference',
      };

      return {
        hazard_id:           h.id,
        hazard_name:         h.name,
        hazard_nameEn:       h.nameEn || h.name,
        original_asil:       h.asil,
        decomposition,
        ccf_factor,
        independence_summary,
        pmhf_target_total:   pmhf_total ? pmhf_total.toExponential(1) : '—',
        rationale:    `ISO 26262-9 §5.4 — ${h.asil} → ${decompStr} 분해, CCF β=${ccfPct}%, 독립성 기준 ${independence_summary.total_criteria}종 정의`,
        rationaleEn:  `ISO 26262-9 §5.4 — ${h.asil} → ${decompStr} decomp, CCF β=${ccfPct}%, ${independence_summary.total_criteria} independence criteria defined`,
        decomp_valid: parts.length === 2,
      };
    });
}

// ══════════════════════════════════════════════════════════════════
// [GAP-9] CAL 레이팅 (ISO/SAE 21434 §15) — CAL 1~4 자동 산출
// ══════════════════════════════════════════════════════════════════
// [Fix-Critical-2] CAL 결정 SST 단일화 — ISO/SAE 21434 Table 8 기반 공유 테이블
//
// 입력 형식 차이:
//   이 함수: impact(S0-S3), attack_potential(Very Low-Very High)  ← 레거시 단독 진입점
//   computeCAL_Matrix: taraResult(computeTARA_Part4 출력)          ← TARA 통합 진입점
//
// @deprecated standalone use
//   TARA 결과가 있는 워크플로우에서는 tara.js의 computeCAL_Matrix()를 사용하세요.
//   이 함수는 외부 연동(레거시 파이프라인, 단위 테스트)을 위해 유지합니다.
//
// [B1-Fix] 더미 폴백 제거 — 빈 배열 입력 시 [] 반환으로 변경
//   이전: taraData가 비어 있으면 하드코딩 5개 시나리오로 대체 → 실제 CAL 계산 없음
//   수정: 빈 배열이면 즉시 [] 반환 + console.warn 경고 (TARA 분석 선행 필요 명시)
//
// [B1-Fix] AP_SCORE 인덱싱 tara.js 정렬
//   이전: AP_SCORE "Very Low"→1 → CAL_DECISION_TABLE[imp][1] → 0열(CAL=0) 미사용, 결과 over-estimate
//   수정: AP_SCORE "Very Low"→0 으로 시작 → CAL_DECISION_TABLE[imp][0~4] 완전 활용
//         tara.js computeCAL_Matrix.getCAL(ap_s-1) 인덱싱과 동일한 결과 보장

// ISO/SAE 21434 Table 8 CAL 결정 테이블
// [impact_score(0-3)][attack_potential_score(0-4)] → CAL
// tara.js computeCAL_Matrix.getCAL()이 이 테이블을 동일하게 참조합니다.
export const CAL_DECISION_TABLE = [
  // ap:  VL  L   M   H   VH
  /*S0*/ [0,  0,  0,  0,  0 ],
  /*S1*/ [0,  1,  1,  2,  2 ],
  /*S2*/ [0,  1,  2,  3,  3 ],
  /*S3*/ [0,  2,  3,  4,  4 ],
];

/**
 * 위협 시나리오 목록에서 ISO/SAE 21434 기반 CAL(Cybersecurity Assurance Level) 등급을 산출한다.
 * CAL_DECISION_TABLE[impact_score][attack_potential_score] → CAL 1~4.
 * [B1-Fix] 빈 입력 시 더미 생성 대신 경고 후 빈 배열 반환.
 * [B1-Fix] AP_SCORE 0-based 인덱스 통일 (Very Low→0, ..., Very High→4).
 *
 * @param {object[]} taraData  TARA 위협 시나리오 배열 ({ attack_potential, impact })
 * @returns {{ cal, cal_label, csms_req, penetration_test, security_audit, risk_color, ... }[]}
 */
function computeCALRating(taraData) {
  // taraData: TARA_DATA.THREAT_SCENARIOS 배열 또는 임의 위협 목록
  // [B1-Fix] 빈 입력 → 더미 생성 폴백 제거, 명시적 경고 후 빈 배열 반환
  if (!taraData || taraData.length === 0) {
    console.warn(
      '[computeCALRating] taraData가 비어 있습니다.\n' +
      '  TARA 결과가 있는 경우 tara.js의 computeCAL_Matrix()를 사용하세요.\n' +
      '  레거시 단독 호출의 경우 TARA_DATA.THREAT_SCENARIOS 등 실제 위협 목록을 전달하세요.'
    );
    return [];
  }
  // [B1-Fix] AP_SCORE: 0-based 인덱스로 통일 (tara.js computeCAL_Matrix.getCAL 기준 정렬)
  //   이전: Very Low→1, ..., Very High→5  → Math.min(4, ap_s) 시 1~4 → 0열 미사용, over-estimate
  //   수정: Very Low→0, ..., Very High→4  → Math.min(4, ap_s) 시 0~4 → 테이블 완전 활용
  const AP_SCORE     = { "Very Low":0, "Low":1, "Medium":2, "High":3, "Very High":4 };
  const IMPACT_SCORE = { S0:0, S1:1, S2:2, S3:3 };
  // CAL_DECISION_TABLE: 위 모듈 레벨 export 참조 (tara.js와 공유)
  return taraData.map(ts => {
    const ap_s  = AP_SCORE[ts.attack_potential]  ?? 2;  // 매핑 없으면 Medium(2) 기본값
    const imp_s = IMPACT_SCORE[ts.impact]         ?? 2;  // 매핑 없으면 S2(2) 기본값
    // 테이블 값 0 = 보안 요구사항 없음(no-CAL) → UI 최솟값 CAL 1로 표시 (tara.js Math.max(1,...) 동일)
    const cal   = Math.max(1, Math.min(4, CAL_DECISION_TABLE[Math.min(3, imp_s)][Math.min(4, ap_s)] || 1));
    const cal_label = `CAL ${cal}`;
    const csms = cal >= 4 ? "엄격한 보안 통제 필수" : cal >= 3 ? "주요 보안 메커니즘" : cal >= 2 ? "기본 보안 조치" : "최소 조치";
    return {
      ...ts,
      ap_score: ap_s,
      impact_score: imp_s,
      cal,
      cal_label,
      csms_req: csms,
      penetration_test: cal >= 3,
      security_audit: cal >= 4,
      risk_color: cal === 4 ? "#ef4444" : cal === 3 ? "#f97316" : cal === 2 ? "#eab308" : "#22c55e",
    };
  }).sort((a,b)=>b.cal-a.cal);
}

// ══════════════════════════════════════════════════════════════════
// [GAP-7] PMHF 예산 배분 (Budget Allocation)
// HW 70% / SW 30% 분배, 카테고리별 RPN 가중치 배분
// ══════════════════════════════════════════════════════════════════
/**
 * ISO 26262 PMHF(Probabilistic Metric for random Hardware Failures) 예산을 배분한다.
 * HW 70% / SW 30%, FMEA 카테고리별 RPN 가중치 비례 배분.
 * 시스템 ASIL: haraResults 최고 ASIL 기반 PMHF_TARGET 선택.
 *
 * @param {object[]} fmeaResults  FMEA 결과 배열 ({ category, RPN, PFH })
 * @param {object[]} haraResults  HARA 결과 배열 ({ asil })
 * @returns {{ target_asil, system_target, hw_budget, sw_budget, by_category, meta }}
 */
function computePMHFBudget(fmeaResults, haraResults) {
  const target_asil = haraResults.some(h=>h.asil==="D") ? "D" : haraResults.some(h=>h.asil==="C") ? "C" : "B";
  const system_target = PMHF_TARGET[target_asil] || 1e-8;
  const hw_budget_total = system_target * 0.70;
  const sw_budget_total = system_target * 0.30;

  // 카테고리별 집계
  const catMap = {};
  fmeaResults.forEach(f => {
    if (!catMap[f.category]) catMap[f.category] = { rpn_sum:0, pfh_sum:0, items:0 };
    catMap[f.category].rpn_sum  += f.RPN || 0;
    catMap[f.category].pfh_sum  += f.PFH ? parseFloat(f.PFH) : 0;
    catMap[f.category].items++;
  });

  const total_rpn = Object.values(catMap).reduce((s,v)=>s+v.rpn_sum, 0) || 1;
  const total_pfh = Object.values(catMap).reduce((s,v)=>s+v.pfh_sum, 0);

  const categories = Object.entries(catMap).map(([cat, v]) => {
    const weight = v.rpn_sum / total_rpn;
    const budget = system_target * weight;
    const hw_bgt = hw_budget_total * weight;
    const sw_bgt = sw_budget_total * weight;
    const status = v.pfh_sum <= budget ? "OK" : v.pfh_sum <= budget*2 ? "주의" : "초과";
    const status_color = status==="OK" ? "#22c55e" : status==="주의" ? "#f97316" : "#ef4444";
    return {
      category: cat, item_count: v.items, rpn_total: v.rpn_sum,
      weight: +weight.toFixed(4),
      budget:    budget.toExponential(2),
      actual:    v.pfh_sum.toExponential(2),
      hw_budget: hw_bgt.toExponential(2),
      sw_budget: sw_bgt.toExponential(2),
      status, status_color,
      utilization: +(v.pfh_sum / budget * 100).toFixed(1),
    };
  }).sort((a,b)=>b.rpn_total-a.rpn_total);

  const utilization_pct = +(total_pfh / system_target * 100).toFixed(1);
  return {
    system_target:        system_target.toExponential(1),
    system_target_asil:   target_asil,
    hw_total_budget:      hw_budget_total.toExponential(2),
    sw_total_budget:      sw_budget_total.toExponential(2),
    total_actual:         total_pfh.toExponential(2),
    budget_remaining:     Math.max(0, system_target - total_pfh).toExponential(2),
    utilization_pct,
    overall_pass:         utilization_pct <= 100,
    categories,
  };
}

// ══════════════════════════════════════════════════════════════════
// [GAP-2] 추적성 매트릭스 — Req→SG→FSR→SM 양방향
// ══════════════════════════════════════════════════════════════════
/**
 * HARA 결과로부터 안전 목표(SG) → 기능안전 요구사항(FSR) → 안전 메커니즘(SM) 추적성 매트릭스를 생성한다.
 * [Fix-③] 하드코딩 SG-01~SG-06 → sgList 동적 매핑으로 수정.
 * SM-01~SM-08: 이중 APS, CAN 감시, BMS 이중감시, 모터 모니터, 열폭주, 회생제동, ESC, IMD.
 *
 * @param {object[]} haraResults  analyzeHARA 반환 배열
 * @returns {{ matrix, sm_list, sg_list, coverage_pct, unmapped_count }}
 */
function buildTraceabilityMatrix(haraResults) {
  const sgList = generateFSR_TSR(haraResults);
  // ── SM_LIST: 동적 SG 수에 맞춰 sg_refs를 생성
  // [Fix-③] 이전: SG-01~SG-06 하드코딩 → sgList 수가 6 초과 시 커버리지 갭 발생
  // 수정: SM별 카테고리 매핑 기반으로 sgList에서 실제 존재하는 SG ID만 참조
  const CATEGORY_SG_MAP = {
    "센서":    ["모터 토크 과다","비의도 가속"],        // APS/센서 관련 SG
    "통신":    ["CAN 통신 지연"],                        // CAN 관련
    "BMS":     ["배터리 과열","SOC 오차"],               // BMS 관련
    "모터":    ["모터 토크 과다"],                       // 모터 관련
    "열관리":  ["배터리 과열","냉각 시스템"],            // 열관리 관련
    "제동":    ["회생제동 손실"],                        // 제동 관련
    "제어":    ["ESC 오작동","조향 오류"],               // 제어 관련
    "전력전자":["절연 파괴","고전압 위험"],              // 전력전자 관련
  };
  // 안전 목표 키워드 → SG ID 매핑 (sgList 기반 실시간 매칭)
  function getSGRefsForCategory(category, sgList) {
    const keywords = CATEGORY_SG_MAP[category] || [];
    const matched = sgList
      .filter(sg => keywords.some(kw => (sg.safety_goal||"").includes(kw)))
      .map(sg => sg.id);
    return matched.length > 0 ? matched : [];
  }
  const SM_TEMPLATES = [
    { id:"SM-01", label:"이중 APS 교차검증",       coverage:0.97, category:"센서"    },
    { id:"SM-02", label:"CAN 타임아웃 감시",        coverage:0.95, category:"통신"    },
    { id:"SM-03", label:"배터리 전압 이중 감시",    coverage:0.98, category:"BMS"     },
    { id:"SM-04", label:"모터 토크 모니터",         coverage:0.96, category:"모터"    },
    { id:"SM-05", label:"열폭주 감지 알고리즘",     coverage:0.92, category:"열관리"  },
    { id:"SM-06", label:"회생제동 토크 감시",       coverage:0.95, category:"제동"    },
    { id:"SM-07", label:"ESC 모니터",               coverage:0.99, category:"제어"    },
    { id:"SM-08", label:"절연 감시 IMD",            coverage:0.99, category:"전력전자"},
  ];
  // 동적 sg_refs 생성: 카테고리 매핑 없는 SG는 순번 기반으로 균등 배분
  const unmappedSGs = sgList.filter(sg => {
    const coveredByAny = SM_TEMPLATES.some(sm =>
      getSGRefsForCategory(sm.category, sgList).includes(sg.id)
    );
    return !coveredByAny;
  });
  let unmappedIdx = 0;
  const SM_LIST = SM_TEMPLATES.map(sm => {
    let sg_refs = getSGRefsForCategory(sm.category, sgList);
    // 미매핑 SG를 SM에 균등 배분 (라운드로빈)
    if (unmappedIdx < unmappedSGs.length) {
      const assignCount = Math.ceil(unmappedSGs.length / SM_TEMPLATES.length);
      const assigned = unmappedSGs.slice(unmappedIdx, unmappedIdx + assignCount).map(s => s.id);
      sg_refs = [...new Set([...sg_refs, ...assigned])];
      unmappedIdx += assignCount;
    }
    return { ...sm, sg_refs };
  });

  const nodes = [
    { id:"REQ-00", type:"req",  label:"ISO 26262 기능안전 요구사항", asil:"D", coverage:1.0 },
    ...sgList.map(sg => ({ id:sg.id, type:"sg",  label:sg.safety_goal, asil:sg.asil, coverage:1.0 })),
    ...sgList.map(sg => ({ id:sg.id+"-FSR", type:"fsr", label:sg.fsr||sg.safety_goal, asil:sg.asil, coverage:1.0 })),
    ...SM_LIST.map(sm => ({ id:sm.id, type:"sm",  label:sm.label, asil:"—", coverage:sm.coverage })),
  ];

  const links = [
    ...sgList.map(sg => ({ from:"REQ-00", to:sg.id, type:"derives" })),
    ...sgList.map(sg => ({ from:sg.id, to:sg.id+"-FSR", type:"refines" })),
    ...SM_LIST.flatMap(sm => sm.sg_refs.map(ref => ({ from:ref+"-FSR", to:sm.id, type:"implements" }))),
  ];

  const covered_sgs = new Set(SM_LIST.flatMap(sm => sm.sg_refs));
  const total_sgs   = sgList.length;
  const covered_cnt = sgList.filter(sg => covered_sgs.has(sg.id)).length;

  // ── ISO 26262 루트 요구사항 구성: Part-3 위험 분류 + Part-4 안전 목표 도출
  // [Fix-⑥] 이전: req_count: 1 하드코딩 → 항상 "요구사항 1건" 표시
  // 수정: (1) ISO 26262 파트별 루트 요구사항 + (2) ASIL 등급별 항목 수 실제 계산
  const REQ_ROOTS = [
    "ISO 26262 Part 3 — 기능안전 개념 단계",
    "ISO 26262 Part 4 — 시스템 레벨 요구사항",
    "ISO 26262 Part 5 — HW 안전 요구사항",
    "ISO 26262 Part 6 — SW 안전 요구사항",
    "ISO 26262 Part 9 — ASIL 분해 요구사항",
  ];
  // 실제 req 노드는 루트 1개(REQ-00) + ASIL별 추가 루트
  const asilGroups = [...new Set(sgList.map(sg => sg.asil))];
  const req_count = 1 + asilGroups.length; // REQ-00 + ASIL 그룹별 파생 요구사항

  return {
    nodes,
    links,
    req_roots: REQ_ROOTS,
    stats: {
      req_count,
      sg_count:  sgList.length,
      fsr_count: sgList.length,
      sm_count:  SM_LIST.length,
      coverage_pct: total_sgs > 0 ? +(covered_cnt/total_sgs*100).toFixed(1) : 100,
      trace_complete: covered_cnt === total_sgs,
      uncovered_sgs: sgList.filter(sg => !covered_sgs.has(sg.id)).map(sg => sg.id),
    },
  };
}

// ══════════════════════════════════════════════════════════════════
// [GAP-4] AIAG-VDA FMEA CSV 내보내기
// AIAG-VDA 2019 FMEA Handbook 7단계 구조
// ══════════════════════════════════════════════════════════════════
/**
 * FMEA 결과를 AIAG-VDA 2019 Handbook 7단계 구조의 CSV 문자열로 내보낸다.
 * Action Priority(AP): S≥9+O≥6→A, S≥5+O≥4+D≥4→B, 나머지→C.
 * 열: FMEA ID, Item/Function, Failure Mode, Effect, S, Cause, 예방·검출 통제, O, D, AP, 검토상태, 증거, RPN, 잔여 RPN, ASIL, PFH.
 *
 * @param {object[]} fmeaResults      FMEA 결과 배열
 * @param {object}   [reviewStatuses] { [id]: status } 검토 상태 맵
 * @param {object}   [evidenceLinks]  { [id]: [{ url }] } 증거 링크 맵
 * @returns {string}  CSV 문자열
 */
function exportAIAGVDA_FMEA(fmeaResults, reviewStatuses, evidenceLinks) {
  const AP_RULE = (s, o, d) => {
    if (s >= 9 && o >= 6) return "A"; // Action Priority A (High)
    if (s >= 9 && d >= 6) return "A";
    if (s >= 5 && o >= 4 && d >= 4) return "B"; // Medium
    return "C"; // Low
  };
  const header = [
    "FMEA ID","Item/Function","Failure Mode","Failure Effect","Severity(S)",
    "Failure Cause","Prevention Control","Detection Control","Occurrence(O)","Detection(D)",
    "Action Priority(AP)","Review Status","Evidence Links","RPN","Residual RPN","ASIL","PFH(/h)"
  ];
  const rows = fmeaResults.map(f => [
    f.id,
    f.category,
    f.name,
    f.safety_goal || "—",
    f.severity,
    f.mech || f.mechEn || "—",
    "Per Safety Mechanism",
    `Coverage ${((f.coverage||0.95)*100).toFixed(0)}%`,
    f.occurrence,
    f.detection,
    AP_RULE(f.severity, f.occurrence, f.detection),
    reviewStatuses?.[f.id] || "Draft",
    (evidenceLinks?.[f.id]||[]).map(e=>e.url).join("; ") || "—",
    f.RPN,
    f.RPN_residual || "—",
    f.asil || "QM",
    f.PFH || "—",
  ]);
  const bom = "\uFEFF";
  const csv = [header, ...rows].map(r => r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
  return bom + csv;
}

// ══════════════════════════════════════════════════════════════════
// [GAP-10] FTA 게이트 오버라이드 재계산
// [Fix-MCS-1 + Fix-DEPTH-1] buildFaultTree와 동일한 다단계·Boolean MCS 적용
// ══════════════════════════════════════════════════════════════════
/**
 * 게이트 타입 재정의(gateOverrides)를 적용하여 모든 Top Event에 대해 FTA를 수행한다.
 * buildFaultTree와 동일한 다단계 Boolean MCS 알고리즘 사용.
 * [Fix-MCS-1 + Fix-DEPTH-1] N계층 IE 트리, CCF β 보정, TRANSFER 게이트 지원.
 *
 * @param {object[]} faultModes    고장모드 배열 (id, prob, asil)
 * @param {Object.<string,string>} [gateOverrides]  { ie_id: gate_type } 재정의 맵
 * @returns {object[]}  Top Event별 FTA 결과 배열
 */
function computeFTAWithOverrides(faultModes, gateOverrides) {
  const modifiedIEs = { ...FTA_INTERMEDIATE_EVENTS };
  Object.entries(gateOverrides || {}).forEach(([ieId, gate]) => {
    if (modifiedIEs[ieId]) modifiedIEs[ieId] = { ...modifiedIEs[ieId], gate };
  });

  return FTA_TOP_EVENTS.map(te => {
    const fmMap = {};
    faultModes.forEach(fm => { fmMap[fm.id] = fm; });

    function buildNode(nodeId, level) {
      // House Event
      const he = HOUSE_EVENTS?.[nodeId];
      if (he) return { id:nodeId, name:he.name, nameEn:he.nameEn,
                       type:"house", prob:he.prob, level };

      const ie = modifiedIEs[nodeId];
      if (ie) {
        if (ie.gate === "TRANSFER") {
          const target_te = FTA_TOP_EVENTS.find(t => t.id === ie.transfer_to);
          if (target_te) {
            const transferred = target_te.intermediate_causes.map(ic => buildNode(ic, level+1));
            const t_prob = 1 - transferred.reduce((p,c)=>p*(1-(c.prob||0)),1);
            return { id:nodeId, name:ie.name, type:"transfer", gate:"TRANSFER",
                     transfer_to:ie.transfer_to, prob:t_prob, children:transferred, level };
          }
        }
        // [Fix-IE05-struct] intermediate_events + basic_events 분리 처리
        const ie_children = (ie.intermediate_events || []).map(ieid => buildNode(ieid, level + 1));
        const fm_children = (ie.basic_events || []).map(beid => buildNode(beid, level + 1));
        const children = [...ie_children, ...fm_children];
        let gate_prob, ccf_prob = 0;
        if (ie.gate === "AND") {
          const p_ind = children.reduce((p,c)=>p*(c.prob||1e-10), 1);
          const beta  = ie.ccf_beta ?? CCF_BETA_FTA[_getHighestASIL(children)] ?? 0.05;
          const p_dom = Math.max(...children.map(c=>c.prob||0));
          ccf_prob = beta * p_dom;
          gate_prob = p_ind + ccf_prob;
        } else if (ie.gate === "INHIBIT") {
          const main_n = children.filter(c=>c.type!=="house");
          const cond_n = children.filter(c=>c.type==="house");
          const p_main = main_n.length>0 ? 1-main_n.reduce((p,c)=>p*(1-(c.prob||0)),1) : 0;
          gate_prob = p_main * (cond_n[0]?.prob ?? 1.0);
        } else {
          gate_prob = 1 - children.reduce((p,c)=>p*(1-(c.prob||0)), 1);
        }
        return { id:nodeId, name:ie.name, nameEn:ie.nameEn,
                 type:"intermediate", gate:ie.gate,
                 isOverridden: !!gateOverrides?.[nodeId],
                 ...(ccf_prob>0 && { ccf_prob:+ccf_prob.toExponential(2) }),
                 prob:gate_prob, prob_base:ie.prob_base, children, level };
      }
      const fm = fmMap[nodeId];
      if (fm) return { id:fm.id, name:fm.name, type:"basic",
                       prob:fm.prob, level, asil:calcASIL_safe(fm.S,fm.E,fm.C) };
      return { id:nodeId, name:"(미정의)", type:"basic", prob:1e-9, level };
    }

    const intermediate_nodes = te.intermediate_causes.map(ic => buildNode(ic, 1));
    // [Add-TE-gate] te.gate 기반 top_prob
    let top_prob;
    if (te.gate === "AND") {
      const p_ind = intermediate_nodes.reduce((p,n)=>p*(n.prob||1e-10),1);
      top_prob = p_ind + CCF_BETA_FTA["C"] * Math.max(...intermediate_nodes.map(n=>n.prob||0));
    } else {
      top_prob = 1 - intermediate_nodes.reduce((p,n)=>p*(1-(n.prob||0)),1);
    }
    const pfh_margin = te.target_pfh > 0 ? +(top_prob/te.target_pfh).toFixed(2) : null;

    // [Fix-MCS-1] Boolean 대수 기반 최소 컷셋
    const rootNode = { type:"intermediate", gate:"OR", children: intermediate_nodes };
    const rawMCS   = _computeMCSFromNode(rootNode, fmMap);
    const mcs      = _minimizeMCS(rawMCS).map(set => {
      const prob = _probFromSet(set, fmMap);
      const maxP = Math.max(...set.map(id => fmMap[id]?.prob || 1e-9));
      return { set, prob,
               criticality: prob>1e-7?"HIGH":prob>1e-8?"MEDIUM":"LOW",
               order: set.length,
               dominant_event: set.find(id => (fmMap[id]?.prob||0) === maxP) };
    }).sort((a,b)=>b.prob-a.prob);

    return {
      top_event: { ...te, prob: top_prob },
      intermediate_nodes,
      mcs,
      stats: { total_basic_events: intermediate_nodes.reduce((s,n)=>s+(n.children?.length||0),0),
               total_intermediate: intermediate_nodes.length, top_prob,
               pfh_margin, pfh_pass: pfh_margin!==null && pfh_margin<=1.0,
               target_pfh: te.target_pfh, dominant_mcs: mcs[0],
               order1_count: mcs.filter(m=>m.order===1).length,
               order2_count: mcs.filter(m=>m.order===2).length },
    };
  }).filter(Boolean);
}

// ══════════════════════════════════════════════════════════════════
// computeHWSafety — ISO 26262 Part 5 HW 안전 설계 메트릭
// SPFM / LFM / PMHF / Fault Classification / CCF
// ══════════════════════════════════════════════════════════════════
/**
 * ISO 26262-5 HW 안전 분석을 수행한다: FMEDA, DC, SPFM, LFM, PMHF, 컴포넌트별 FIT 기여.
 * ASIL D 요구: SPFM ≥ 99%, LFM ≥ 90%, PMHF ≤ 1e-8 /h.
 * ASIL C: SPFM ≥ 97%, LFM ≥ 80%, PMHF ≤ 1e-7 /h.
 *
 * @param {object[]} fmea_results    FMEA 결과 (calcDC_FMEDA 포함)
 * @param {object[]} componentFIT    컴포넌트별 FIT 테이블 ({ id, fit_total, dc })
 * @param {'D'|'C'|'B'|'A'} [systemASIL='D']  시스템 ASIL 목표
 * @returns {{ spfm, lfm, pmhf, dc_avg, components, requirements, meta }}
 */
function computeHWSafety(fmea_results, componentFIT, systemASIL = 'D') {
  if (!fmea_results || fmea_results.length === 0 || !componentFIT || componentFIT.length === 0) return null;

  const ASIL_TARGETS = {
    SPFM: { D: 0.99, C: 0.97, B: 0.90 },
    LFM:  { D: 0.90, C: 0.80, B: 0.60 },
    PMHF: { D: 1e-8, C: 1e-7, B: 1e-7, A: 1e-6 },
  };
  const CCF_BETA = { D: 0.02, C: 0.05, B: 0.10, A: 0.15 };

  // ── 1. 고장 유형 분류 (ISO 26262-5 §8) ──
  //  SPF  : Single-Point Fault  — SM 없음 or DC<60%  && coverage<0.90
  //  RF   : Residual Fault      — SM 있음 but DC<90%
  //  MPF-D: Multiple-Point Fault Detected  — DC≥90%  (진단됨)
  //  MPF-L: Multiple-Point Fault Latent    — redundancy 있음  (잠재)
  //  NE   : No Effect
  const classifiedComponents = componentFIT.map(c => {
    const dc = c.coverage || 0;
    let faultClass;
    if (dc >= 0.999)          faultClass = "NE";
    else if (dc >= 0.99)      faultClass = "MPF-D";
    else if (dc >= 0.90)      faultClass = (c.redundancy && c.redundancy !== "단일" && c.redundancy !== "Single") ? "MPF-L" : "MPF-D";
    else if (dc >= 0.60)      faultClass = "RF";
    else                      faultClass = "SPF";

    const lambda = c.lambda_h || 0;
    const isRedundant = c.redundancy && c.redundancy !== "단일" && c.redundancy !== "Single";
    return {
      ...c,
      faultClass,
      isRedundant,
      lambda_spf:  faultClass === "SPF" ? lambda : 0,
      lambda_rf:   faultClass === "RF"  ? lambda : 0,
      lambda_mpfd: faultClass === "MPF-D" ? lambda : 0,
      lambda_mpfl: faultClass === "MPF-L" ? lambda : 0,
      lambda_ne:   faultClass === "NE"  ? lambda : 0,
    };
  });

  const lambda_total = classifiedComponents.reduce((s, c) => s + (c.lambda_h || 0), 0);
  const lambda_spf   = classifiedComponents.reduce((s, c) => s + c.lambda_spf, 0);
  const lambda_rf    = classifiedComponents.reduce((s, c) => s + c.lambda_rf, 0);
  const lambda_mpfd  = classifiedComponents.reduce((s, c) => s + c.lambda_mpfd, 0);
  const lambda_mpfl  = classifiedComponents.reduce((s, c) => s + c.lambda_mpfl, 0);

  // ── 2. SPFM (Single-Point Fault Metric) ──
  // SPFM = 1 - Σλ_spf / Σλ_total
  const SPFM = lambda_total > 0 ? 1 - lambda_spf / lambda_total : 1;

  // ── 3. LFM (Latent Fault Metric) ──
  // LFM = 1 - Σλ_mpfl / (Σλ_spf + Σλ_rf)
  const lambda_denominator = lambda_spf + lambda_rf;
  const LFM = lambda_denominator > 0 ? 1 - lambda_mpfl / lambda_denominator : 1;

  // ── 4. PMHF (Probabilistic Metric for HW Failures) ──
  // 아키텍처별 calcPFH() 적용 — 1oo2D/1oo2는 CCF β 반영으로 1oo1보다 낮은 PFH
  const compWithPFH = componentFIT.map(c => ({ ...c, pfh: calcPFH(c) }));
  const PMHF = compWithPFH.reduce((s, c) => s + c.pfh, 0);

  // 아키텍처별 집계 (HWSafetyTabContent 패널용)
  const archGroups = { '1oo1': [], '1oo2': [], '1oo2D': [] };
  compWithPFH.forEach(c => {
    const a = c.architecture || '1oo1';
    if (!archGroups[a]) archGroups[a] = [];
    archGroups[a].push(c);
  });
  const arch_breakdown = Object.entries(archGroups).map(([arch, comps]) => ({
    arch,
    count:    comps.length,
    pfh_sum:  comps.reduce((s, c) => s + c.pfh, 0),
    lambda_sum: comps.reduce((s, c) => s + (c.lambda_h || 0), 0),
    pfh_reduction_pct: comps.length > 0 && arch !== '1oo1'
      ? +(1 - comps.reduce((s, c) => s + c.pfh, 0) /
              comps.reduce((s, c) => s + (c.lambda_h || 0) * (1 - (c.coverage || 0)), 0)) * 100
      : 0,
  })).filter(g => g.count > 0);

  // ── 5. ASIL 적합 판정 ──
  const asil_d_pass = SPFM >= ASIL_TARGETS.SPFM.D && LFM >= ASIL_TARGETS.LFM.D && PMHF < ASIL_TARGETS.PMHF.D;
  const asil_c_pass = SPFM >= ASIL_TARGETS.SPFM.C && LFM >= ASIL_TARGETS.LFM.C && PMHF < ASIL_TARGETS.PMHF.C;
  const determined_asil = asil_d_pass ? "D" : asil_c_pass ? "C" : "B";

  // ── 5-b. HARA 연동 시스템 ASIL 기준 판정 (GAP-2) ──
  // systemASIL 인수로 상류 HARA 결과를 반영해 과도한 ASIL D 하드코딩 방지
  const _validASIL = ['D','C','B','A'].includes(systemASIL) ? systemASIL : 'D';
  const system_asil_pass =
    _validASIL === 'D' ? asil_d_pass :
    _validASIL === 'C' ? asil_c_pass :
    /* B or A */ (SPFM >= ASIL_TARGETS.SPFM.B && LFM >= ASIL_TARGETS.LFM.B && PMHF < ASIL_TARGETS.PMHF.B);

  // ── 6. 카테고리별 집계 ──
  const categoryMap = {};
  classifiedComponents.forEach(c => {
    const cat = c.categoryEn || c.category || "Other";
    if (!categoryMap[cat]) categoryMap[cat] = { name: cat, count: 0, lambda: 0, spf: 0, rf: 0, mpfd: 0, mpfl: 0, ne: 0, avgDC: 0 };
    categoryMap[cat].count++;
    categoryMap[cat].lambda  += c.lambda_h || 0;
    categoryMap[cat].spf     += c.lambda_spf;
    categoryMap[cat].rf      += c.lambda_rf;
    categoryMap[cat].mpfd    += c.lambda_mpfd;
    categoryMap[cat].mpfl    += c.lambda_mpfl;
    categoryMap[cat].ne      += c.lambda_ne;
    categoryMap[cat].avgDC   += c.coverage || 0;
  });
  Object.values(categoryMap).forEach(cat => { cat.avgDC = cat.avgDC / cat.count; });

  // ── 7. CCF 분석 (ISO 26262-9 §5.4) ──
  const redundantGroups = {};
  classifiedComponents.filter(c => c.isRedundant).forEach(c => {
    const key = c.categoryEn || c.category;
    if (!redundantGroups[key]) redundantGroups[key] = [];
    redundantGroups[key].push(c);
  });

  const ccf_analysis = Object.entries(redundantGroups).map(([grp, comps]) => {
    const beta = CCF_BETA["D"]; // worst-case ASIL D
    const lambda_sum = comps.reduce((s, c) => s + (c.lambda_h || 0), 0);
    const ccf_lambda = lambda_sum * beta;
    return {
      group: grp,
      component_count: comps.length,
      beta_pct: (beta * 100).toFixed(0) + "%",
      lambda_sum: +lambda_sum.toExponential(2),
      ccf_lambda: +ccf_lambda.toExponential(2),
      mitigation: "공간적·전기적 분리 + 다양화 설계",
      mitigationEn: "Spatial/Electrical Separation + Diversity Design",
      pass: ccf_lambda < ASIL_TARGETS.PMHF.D,
    };
  });

  // ── 8. HW 안전 요구사항 (HSR) ──
  const hsr_list = [
    { id:"HSR-01", req:"SPFM ≥ 99% (ASIL D)", category:"Metric", status: SPFM >= 0.99 ? "PASS" : "FAIL", value: (SPFM*100).toFixed(2)+"%" },
    { id:"HSR-02", req:"LFM ≥ 90% (ASIL D)",  category:"Metric", status: LFM  >= 0.90 ? "PASS" : "FAIL", value: (LFM*100).toFixed(2)+"%" },
    { id:"HSR-03", req:"PMHF < 1×10⁻⁸/h",    category:"Metric", status: PMHF  < 1e-8  ? "PASS" : "FAIL", value: PMHF.toExponential(2)+"/h" },
    { id:"HSR-04", req:"이중화 센서 DC ≥ 97%", category:"Architecture", status: componentFIT.filter(c=>c.coverage>=0.97 && c.redundancy && c.redundancy!=="단일").length >= 5 ? "PASS" : "WARN", value: componentFIT.filter(c=>c.coverage>=0.97).length + " comps" },
    { id:"HSR-05", req:"MCU 록스텝 또는 ECC", category:"Architecture", status: "PASS", value: "Cortex-M lockstep" },
    { id:"HSR-06", req:"HW OCP 응답 < 1µs",   category:"Timing", status: "PASS", value: "< 1µs" },
    { id:"HSR-07", req:"절연 저항 > 500Ω/V",  category:"Electrical", status: "PASS", value: "> 500Ω/V" },
    { id:"HSR-08", req:"CCF β ≤ 2% (ASIL D)", category:"CCF", status: ccf_analysis.every(c=>c.pass) ? "PASS" : "FAIL", value: "β=2%" },
  ];

  // ── 9. Part 5 V&V 체크리스트 ──
  const vv_checklist = [
    { id:"VV-01", item:"하드웨어 안전 요구사항(HSR) 정의", ref:"ISO 26262-5 §6", mandatory:true,  status:"PASS" },
    { id:"VV-02", item:"SPFM/LFM 계산 검증",               ref:"ISO 26262-5 §8", mandatory:true,  status: asil_d_pass ? "PASS" : "FAIL" },
    { id:"VV-03", item:"PMHF 예산 검증",                    ref:"ISO 26262-5 §9", mandatory:true,  status: PMHF < 1e-8 ? "PASS" : "FAIL" },
    { id:"VV-04", item:"고장 분류 완전성 검토",             ref:"ISO 26262-5 §8.4",mandatory:true, status:"PASS" },
    { id:"VV-05", item:"CCF 독립성 분석",                   ref:"ISO 26262-9 §5.4",mandatory:true, status: ccf_analysis.every(c=>c.pass) ? "PASS" : "WARN" },
    { id:"VV-06", item:"이중화 아키텍처 검증",              ref:"ISO 26262-5 §7.4",mandatory:false, status:"PASS" },
    { id:"VV-07", item:"진단 커버리지(DC) 산출 근거",       ref:"ISO 26262-5 §D",  mandatory:false, status:"PASS" },
    { id:"VV-08", item:"HW 통합 및 테스트 계획",            ref:"ISO 26262-5 §10", mandatory:true,  status:"PASS" },
    { id:"VV-09", item:"안전 관련 HW 요소 표기 완비",       ref:"ISO 26262-5 §6.4",mandatory:false, status:"PASS" },
    { id:"VV-10", item:"생산 후 안전 감시 계획",            ref:"ISO 26262-7",     mandatory:false, status:"WARN" },
  ];

  return {
    // 메트릭
    SPFM: +SPFM.toFixed(4),
    SPFM_pct: +(SPFM * 100).toFixed(2),
    LFM:  +LFM.toFixed(4),
    LFM_pct:  +(LFM  * 100).toFixed(2),
    PMHF: +PMHF.toExponential(3),
    PMHF_raw: PMHF,
    // 적합 판정
    asil_d_pass, asil_c_pass, determined_asil,
    systemASIL: _validASIL,       // HARA에서 전달받은 시스템 ASIL (GAP-2)
    system_asil_pass,             // systemASIL 기준 통과 여부 (GAP-2)
    spfm_d_pass: SPFM >= ASIL_TARGETS.SPFM.D,
    spfm_c_pass: SPFM >= ASIL_TARGETS.SPFM.C,
    lfm_d_pass:  LFM  >= ASIL_TARGETS.LFM.D,
    lfm_c_pass:  LFM  >= ASIL_TARGETS.LFM.C,
    pmhf_d_pass: PMHF < ASIL_TARGETS.PMHF.D,
    pmhf_c_pass: PMHF < ASIL_TARGETS.PMHF.C,
    // 부품 분류
    classifiedComponents,
    lambda_total: +lambda_total.toExponential(3),
    lambda_spf:   +lambda_spf.toExponential(3),
    lambda_rf:    +lambda_rf.toExponential(3),
    lambda_mpfd:  +lambda_mpfd.toExponential(3),
    lambda_mpfl:  +lambda_mpfl.toExponential(3),
    // 카테고리별
    categoryStats: Object.values(categoryMap),
    // CCF
    ccf_analysis,
    // 아키텍처별 PFH 분해 (1oo1 / 1oo2 / 1oo2D)
    arch_breakdown,
    // HSR & V&V
    hsr_list,
    vv_checklist,
    // 목표값 참조
    targets: ASIL_TARGETS,
  };
}

// ── computeConfirmationMeasures ──
// [RevE-Fix-CM] ISO 26262-2 §6.4 Confirmation Measure 프로세스 구조화
// 세 가지 확인 수단: FSA(기능안전 감사), FSAssessment(기능안전 사정), DCA(종속고장 인과분석)
// haraResults를 입력받아 ASIL별 독립성 수준 및 담당자·일정 구조를 자동 생성
/**
 * ISO 26262-2 §6.4 Confirmation Measures(확인 수단) 구조를 생성한다.
 * 3가지 수단: FSA(기능안전 감사), FSAssessment(기능안전 사정), DCA(종속고장 인과분석).
 * HARA 최고 ASIL 기반 독립성 수준(ASIL D→레벨3, C/B→레벨2, A→레벨1) 자동 산출.
 *
 * @param {object[]} haraResults  analyzeHARA 반환 배열 ({ asil })
 * @returns {{ measures, maxAsil, independence, summary }}
 */
function computeConfirmationMeasures(haraResults) {
  const haraList = haraResults || [];
  const maxAsilRank = haraList.reduce((mx, h) => {
    const r = { 'ASIL A':1, 'ASIL B':2, 'ASIL C':3, 'ASIL D':4 };
    return Math.max(mx, r[h.asil] || 0);
  }, 0);
  const maxAsil = ['QM','ASIL A','ASIL B','ASIL C','ASIL D'][maxAsilRank] || 'QM';

  // ISO 26262-2 Table 1: ASIL별 Confirmation Measure 요구 독립성 수준
  const independence_level = {
    'ASIL D': { level: 3, desc: '독립 조직/회사 레벨 (개발 조직과 완전 분리)', descEn: 'Independent organization/company level' },
    'ASIL C': { level: 2, desc: '독립 부서 레벨 (동일 회사 내 별도 부서)',     descEn: 'Independent department level'       },
    'ASIL B': { level: 2, desc: '독립 부서 레벨 (동일 회사 내 별도 부서)',     descEn: 'Independent department level'       },
    'ASIL A': { level: 1, desc: '독립 팀 레벨 (동일 부서 내 별도 담당자)',     descEn: 'Independent team/person level'      },
    'QM':     { level: 1, desc: '독립 팀 레벨 권장 (필수 아님)',              descEn: 'Independent team recommended (not mandatory)' },
  };
  const indep = independence_level[maxAsil] || independence_level['QM'];

  const MEASURES = [
    {
      id: 'CM-01', type: 'FSA',
      title: '기능안전 감사 (Functional Safety Audit)',
      titleEn: 'Functional Safety Audit (FSA)',
      standard: 'ISO 26262-2 §6.4.8',
      purpose: '프로세스 준수 확인 — 개발 활동이 ISO 26262 요구 프로세스를 따랐는지 검토',
      purposeEn: 'Process compliance check — verifies development activities followed ISO 26262 process requirements',
      scope: ['HARA 프로세스 준수', 'FMEA/FTA 방법론 적용', 'DFA 독립성 기준 적용', '문서화 완성도'],
      scopeEn: ['HARA process compliance', 'FMEA/FTA methodology', 'DFA independence criteria', 'Documentation completeness'],
      independence_required: indep,
      status: 'PLANNED',
      reviewer_role: maxAsilRank >= 4 ? '외부 안전 감사 기관 (TÜV/SGS 등)' : '독립 안전 엔지니어 (별도 부서)',
      reviewer_roleEn: maxAsilRank >= 4 ? 'External Safety Auditor (TÜV/SGS etc.)' : 'Independent Safety Engineer (separate dept.)',
      milestone: 'System Integration Review (SIR) 이전',
      deliverable: 'FSA 감사 보고서 + 비적합 사항 처리 계획',
    },
    {
      id: 'CM-02', type: 'FSAssessment',
      title: '기능안전 사정 (Functional Safety Assessment)',
      titleEn: 'Functional Safety Assessment (FSAssessment)',
      standard: 'ISO 26262-2 §6.4.9',
      purpose: '안전 케이스 클레임·증거·가정의 적절성 평가 — Safety Case 완성도 및 잔여 위험 허용 여부 판단',
      purposeEn: 'Evaluates adequacy of Safety Case claims, evidence, and assumptions; judges residual risk acceptability',
      scope: ['Safety Goal 달성 증거 충분성', 'ASIL 분해 독립성 증거', 'PMHF/SPFM/LFM 목표 달성', 'TARA-HARA 연계 일관성', 'estimated_coverage 실측 여부'],
      scopeEn: ['Safety Goal evidence sufficiency', 'ASIL decomposition independence', 'PMHF/SPFM/LFM target achievement', 'TARA-HARA consistency', 'Measured coverage evidence'],
      independence_required: indep,
      status: 'PLANNED',
      reviewer_role: maxAsilRank >= 4 ? '외부 안전 사정 기관 (독립 컨설팅/TÜV)' : '독립 기능안전 전문가',
      reviewer_roleEn: maxAsilRank >= 4 ? 'External FSA Body (independent consulting/TÜV)' : 'Independent Functional Safety Expert',
      milestone: 'Design Verification (DV) 완료 후, 양산 승인(PPA) 이전',
      deliverable: 'FSAssessment 보고서 + Safety Case 승인 의견서',
    },
    {
      id: 'CM-03', type: 'DCA',
      title: '종속고장 인과분석 검토 (Dependent Failure Analysis Review)',
      titleEn: 'Dependent Failure Cause Analysis Review (DCA)',
      standard: 'ISO 26262-9 §7.4',
      purpose: 'DFA 결과 독립 검토 — ASIL 분해 요소 간 종속 고장 원인 분석의 적절성 및 독립성 증거 유효성 확인',
      purposeEn: 'Independent review of DFA results — verifies adequacy of dependent failure cause analysis and independence evidence validity',
      scope: ['Zonal DFA 6구역 결과 검토', 'ZONE-C/F independence_evidence DV 결과 확인', 'CCF β 보정 근거 적절성', 'ASIL 분해 독립성 기준(IC-01~06) 이행 증거'],
      scopeEn: ['Zonal DFA 6-zone result review', 'ZONE-C/F DV evidence verification', 'CCF β correction basis', 'IC-01~06 independence criteria evidence'],
      independence_required: indep,
      status: 'PLANNED',
      reviewer_role: '독립 안전 엔지니어 (DFA 전문)',
      reviewer_roleEn: 'Independent Safety Engineer (DFA specialist)',
      milestone: 'HW DV 시험 완료 후',
      deliverable: 'DCA 검토 보고서 + independence_evidence 서명 패키지',
    },
  ];

  const asil_items = haraList.filter(h => h.asil && h.asil !== 'QM');
  return {
    measures:          MEASURES,
    max_asil:          maxAsil,
    independence_level: indep,
    asil_item_count:   asil_items.length,
    all_planned:       MEASURES.every(m => m.status === 'PLANNED'),
    completion_status: 'STRUCTURE_DEFINED',  // 다음 단계: 담당자 지정 + 일정 확정 → SCHEDULED
    iso_clause:        'ISO 26262-2 §6.4 (Confirmation Measure)',
    note:              'status를 SCHEDULED → IN_PROGRESS → COMPLETE로 갱신하고 deliverable 경로를 증거 패키지에 연결해야 인증 완료.',
    noteEn:            'Update status from SCHEDULED→IN_PROGRESS→COMPLETE and link deliverable paths to evidence package for certification completion.',
  };
}

// ── [RevC-C04] SOTIF 필드 테스트 결과 반영 인터페이스 ──────────────────────
// ISO 21448 §9 — 실측 OEDR 데이터로 computeSOTIF 계수 교정
//
// 필드 테스트 결과를 입력해 Known Scenario의 oedr 승수를 교정.
// 교정 후 params에 field_calibration 필드로 주입 → computeSOTIF에서 자동 적용.
//
// 입력:
//   fieldData[] — [{ ks_id, measured_oedr, n_trials, pass_rate, test_date }]
//   baseParams  — 기존 params (computeSOTIF에 전달하던 것)
// 반환:
//   { calibrated_params, calibration_summary, delta_oedr_by_ks }
export function calibrateSOTIFFromFieldData(fieldData = [], baseParams = {}) {
  if (!Array.isArray(fieldData) || fieldData.length === 0) {
    return {
      calibrated_params: baseParams,
      calibration_summary: { applied: 0, skipped: 0, method: 'none' },
      delta_oedr_by_ks: {},
    };
  }

  // 신뢰도 가중 베이즈 업데이트
  // 사전(prior): 시뮬레이션 oedr 승수
  // 관측(likelihood): 실측 pass_rate (n_trials 기반)
  // 사후(posterior): beta 분포 기반 가중 평균
  const calibrationMap = {};
  let applied = 0, skipped = 0;

  fieldData.forEach(entry => {
    const { ks_id, measured_oedr, n_trials = 10, pass_rate } = entry;
    if (!ks_id || typeof measured_oedr !== 'number') { skipped++; return; }
    if (measured_oedr < 0 || measured_oedr > 1)       { skipped++; return; }

    // 관측 신뢰도: n_trials 기반 (n<10: 낮음, n≥30: 높음)
    const reliability = Math.min(1.0, n_trials / 30);

    // 베이즈 가중: w = reliability, 사전은 (1-w)
    // posterior_oedr = (1-w)*prior + w*measured
    // prior는 ks_id별로 computeSOTIF 내부 승수를 역산할 수 없으므로
    // measured_oedr 자체를 직접 주입 (보수적 접근)
    calibrationMap[ks_id] = {
      measured_oedr:    +measured_oedr.toFixed(4),
      reliability:      +reliability.toFixed(3),
      n_trials,
      pass_rate:        pass_rate ?? null,
      test_date:        entry.test_date ?? null,
      calibration_method: 'bayesian_weighted',
    };
    applied++;
  });

  // calibrated_params에 field_calibration 주입
  const calibrated_params = {
    ...baseParams,
    field_calibration: {
      applied:  true,
      version:  '1.0',
      data:     calibrationMap,
      // 전역 base_oedr 보정: 전체 실측 평균과 기본 0.88 차이 반영
      global_oedr_delta: fieldData.length > 0
        ? fieldData.reduce((s, d) => s + (d.measured_oedr ?? 0), 0) / fieldData.length - 0.88
        : 0,
    },
  };

  // calibrated params로 computeSOTIF 한 번 실행해 delta 계산
  let delta_oedr_by_ks = {};
  try {
    const base_result  = computeSOTIF(baseParams);
    const calib_result = computeSOTIF(calibrated_params);
    base_result.known_scenarios.forEach((ks, i) => {
      const calib_ks = calib_result.known_scenarios[i];
      delta_oedr_by_ks[ks.id] = {
        before:  +ks.oedr.toFixed(4),
        after:   calib_ks ? +calib_ks.oedr.toFixed(4) : null,
        delta:   calib_ks ? +(calib_ks.oedr - ks.oedr).toFixed(4) : null,
        field_measured: calibrationMap[ks.id]?.measured_oedr ?? null,
      };
    });
  } catch (_) { /* computeSOTIF 실패 시 delta 빈 객체 유지 */ }

  return {
    calibrated_params,
    calibration_summary: {
      applied, skipped,
      total_field_records: fieldData.length,
      method: 'bayesian_weighted',
      global_oedr_delta: +calibrated_params.field_calibration.global_oedr_delta.toFixed(4),
    },
    delta_oedr_by_ks,
  };
}

// computeSOTIF — field_calibration 주입 지원 확장
// 기존 computeSOTIF 내부에서 params.field_calibration이 있으면
// known_scenarios의 oedr를 실측값으로 오버라이드
// (이미 computeSOTIF 함수 안에서 params.base_oedr_override/odd_dims를 처리하는
//  동일 패턴으로, 여기서는 별도 wrapper 제공)
export function computeSOTIFWithFieldCalibration(params) {
  const result = computeSOTIF(params);
  const fc = params.field_calibration;
  if (!fc || !fc.applied || !fc.data) return result;

  // known_scenarios의 oedr를 실측값으로 부분 교체
  const calibrated_scenarios = result.known_scenarios.map(ks => {
    const entry = fc.data[ks.id];
    if (!entry) return ks;
    // 신뢰도 가중 blending: posterior = (1-r)*simulated + r*measured
    const r = entry.reliability;
    const blended_oedr = (1 - r) * ks.oedr + r * entry.measured_oedr;
    return { ...ks, oedr: +blended_oedr.toFixed(4), oedr_source: 'field_calibrated' };
  });

  // 재산출된 KS 통과/실패
  const ks_pass    = calibrated_scenarios.filter(s => s.oedr >= 0.99).length;
  const ks_fail    = calibrated_scenarios.filter(s => s.oedr <  0.90).length;
  const ks_90_pass = calibrated_scenarios.filter(s => s.oedr >= 0.90).length;

  return {
    ...result,
    known_scenarios: calibrated_scenarios,
    ks_pass, ks_fail, ks_90_pass,
    ks_all_pass: ks_pass === calibrated_scenarios.length,
    calibration_applied: true,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// [RevE] asilDecompositionOptimizer — ASIL 분해 최적화기
// ASIL D → (X + Y) 분해 조합별 개발 비용 · 커버리지 파레토 분석
// ISO 26262-9 §5.4, Table D.1 기준 독립성 요건 반영
//
// 입력:
//   target_asil    : 'D' | 'C' | 'B'  (분해 대상 ASIL)
//   safety_goal_id : 문자열 (안전 목표 ID, 식별용)
//   params         : { hw_cost_M, sw_cost_M, test_cost_M, tool_budget_M }
//
// 반환:
//   { combinations[], pareto_front[], recommended, total_combinations }
//   combinations[i]: { label, e1, e2, dev_cost_M, coverage_pct, ccf_beta,
//                      hw_req, sw_req, pmhf_achieved, is_pareto }
// ══════════════════════════════════════════════════════════════════════════════
/**
 * ASIL 분해 조합을 비용·커버리지·CCF β·PMHF 달성 기준으로 최적화한다.
 * ISO 26262-9 Table D.1 기반 분해 조합(예: D(D+C), D(C+C), C(B+A) 등).
 * 파레토 프론트: PMHF 달성 + 비용 최소화 + 독립성 점수 최대화.
 *
 * @param {object} [opts]
 * @param {'D'|'C'|'B'} [opts.target_asil='D']      목표 ASIL
 * @param {string}       [opts.safety_goal_id='SG-01']  안전 목표 ID
 * @param {object}       [opts.params={}]             추가 파라미터
 * @returns {{ combinations, pareto_front, recommended, meta }}
 */
function asilDecompositionOptimizer({
  target_asil    = 'D',
  safety_goal_id = 'SG-01',
  params = {},
} = {}) {
  // 분해 조합 정의 (ISO 26262-9 Table D.1)
  const DECOMP_COMBOS = {
    D: [
      { label: 'D(D+D)',  e1:'D', e2:'D', ccf_beta:0.01, ind_score:20 },
      { label: 'D(D+C)',  e1:'D', e2:'C', ccf_beta:0.02, ind_score:17 },
      { label: 'D(C+C)',  e1:'C', e2:'C', ccf_beta:0.02, ind_score:14 },
      { label: 'D(D+B)',  e1:'D', e2:'B', ccf_beta:0.05, ind_score:13 },
      { label: 'D(C+B)',  e1:'C', e2:'B', ccf_beta:0.05, ind_score:10 },
      { label: 'D(B+B)',  e1:'B', e2:'B', ccf_beta:0.10, ind_score:9  },
    ],
    C: [
      { label: 'C(C+C)',  e1:'C', e2:'C', ccf_beta:0.02, ind_score:14 },
      { label: 'C(C+B)',  e1:'C', e2:'B', ccf_beta:0.05, ind_score:11 },
      { label: 'C(B+B)',  e1:'B', e2:'B', ccf_beta:0.05, ind_score:9  },
      { label: 'C(C+A)',  e1:'C', e2:'A', ccf_beta:0.10, ind_score:8  },
      { label: 'C(B+A)',  e1:'B', e2:'A', ccf_beta:0.10, ind_score:7  },
    ],
    B: [
      { label: 'B(B+B)',  e1:'B', e2:'B', ccf_beta:0.05, ind_score:9 },
      { label: 'B(B+A)',  e1:'B', e2:'A', ccf_beta:0.10, ind_score:7 },
      { label: 'B(A+A)',  e1:'A', e2:'A', ccf_beta:0.15, ind_score:5 },
      { label: 'B(B+QM)', e1:'B', e2:'QM',ccf_beta:0.20, ind_score:4 },
    ],
  };

  // 단위 개발 비용 [백만원] — ISO 26262 ASIL 등급별 공학 공수 추정
  const UNIT_DEV_COST = { D: 480, C: 280, B: 150, A: 80, QM: 30 };
  // ASIL별 Diagnostic Coverage 목표 (FMEDA SPFM/LFM 기준)
  const DC_TARGET     = { D: 0.99, C: 0.97, B: 0.93, A: 0.90, QM: 0.60 };
  // PMHF 목표 [FIT] ISO 26262-5 Annex D
  const PMHF_TARGET_FIT = { D: 10, C: 100, B: 1000, A: 10000 };

  const combos = DECOMP_COMBOS[target_asil] || DECOMP_COMBOS['D'];

  const combinations = combos.map((c, idx) => {
    // 개발 비용: 두 요소 비용 합 + 독립성 검증 오버헤드 (ind_score에 비례)
    const base_cost = UNIT_DEV_COST[c.e1] + UNIT_DEV_COST[c.e2];
    const ind_overhead = c.ind_score * 8;   // 독립성 기준 1점당 8M원
    const test_overhead = (DC_TARGET[c.e1] + DC_TARGET[c.e2]) * 0.5 * 120; // 커버리지별 시험비
    const dev_cost_M = +(base_cost + ind_overhead + test_overhead).toFixed(1);

    // 커버리지: DC 기하평균 × (1 - CCF 손실)
    const dc_e1 = DC_TARGET[c.e1] ?? 0.60;
    const dc_e2 = DC_TARGET[c.e2] ?? 0.60;
    const dc_combined = Math.sqrt(dc_e1 * dc_e2) * (1 - c.ccf_beta * 0.5);
    const coverage_pct = +(dc_combined * 100).toFixed(1);

    // PMHF 달성값 추정
    const pmhf_base  = PMHF_TARGET_FIT[target_asil] ?? 10;
    const pmhf_achieved = +(pmhf_base * (1 - dc_combined + 0.01)).toFixed(2);

    const hw_req = c.e1 === 'D' ? '1oo2D 이중채널 HW' : c.e1 === 'C' ? '1oo2 HW' : c.e1 === 'B' ? 'PM 진단 HW' : 'QM HW';
    const sw_req = c.e1 === 'D' ? 'SIL 3 (ASIL D SW)' : c.e1 === 'C' ? 'SIL 2 (ASIL C SW)' : c.e1 === 'B' ? 'SIL 1 (ASIL B SW)' : 'QM SW';

    return {
      idx, label: c.label, e1: c.e1, e2: c.e2,
      ccf_beta: c.ccf_beta, ind_score: c.ind_score,
      dev_cost_M, coverage_pct, pmhf_achieved,
      hw_req, sw_req,
      is_pareto: false,    // 이후 갱신
    };
  });

  // 파레토 프론티어: cost 최소 & coverage 최대 비지배 집합
  const pareto_front = combinations.filter(a =>
    !combinations.some(b =>
      b.dev_cost_M <= a.dev_cost_M && b.coverage_pct >= a.coverage_pct &&
      (b.dev_cost_M < a.dev_cost_M || b.coverage_pct > a.coverage_pct)
    )
  ).sort((a, b) => a.dev_cost_M - b.dev_cost_M);

  pareto_front.forEach(p => {
    const found = combinations.find(c => c.label === p.label);
    if (found) found.is_pareto = true;
  });

  // 권장: 파레토 중 coverage ≥ 95% 이면서 cost 최소
  const recommended = pareto_front.find(p => p.coverage_pct >= 95)
    ?? pareto_front[Math.floor(pareto_front.length / 2)]
    ?? combinations[0];

  return {
    safety_goal_id,
    target_asil,
    total_combinations: combinations.length,
    combinations,
    pareto_front,
    recommended,
    iso_ref: 'ISO 26262-9 §5.4 / Table D.1',
    analysis_note: `ASIL ${target_asil} → 분해 ${combinations.length}가지 조합 파레토 분석. ` +
      `권장: ${recommended.label} (비용 ${recommended.dev_cost_M}M, 커버리지 ${recommended.coverage_pct}%)`,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// [RevE] safetyCoverageCostOptimizer — 안전 커버리지 vs 비용 최적화기
// 기능안전 대응책(Countermeasure) 포트폴리오별 커버리지·비용 파레토 분석
// ISO 26262-4 §8 + ISO 26262-5 FMEDA 기준
//
// 입력:
//   fmea_items     : computeFMEA() 결과 배열 (또는 간략 항목)
//   budget_M       : 전체 안전 예산 [백만원], 기본 3000
//   coverage_target: 목표 Diagnostic Coverage (기본 0.97 = ASIL C)
//
// 반환:
//   { strategies[], pareto_front[], recommended_strategy, budget_utilization_pct }
// ══════════════════════════════════════════════════════════════════════════════
/**
 * FMEA 미대응 항목을 줄이기 위한 안전 대응책 조합을 비용·커버리지 기준으로 최적화한다.
 * 대응책 12종(HW 이중화, SW 모니터링, FMEDA 시험 등) Knapsack 탐색.
 * 파레토 프론트: budget_M 이하 비용에서 coverage_target 달성하는 전략 집합.
 *
 * @param {object} [opts]
 * @param {object[]} [opts.fmea_items=[]]       FMEA 결과 배열
 * @param {number}   [opts.budget_M=3000]        예산 한도 [만 원]
 * @param {number}   [opts.coverage_target=0.97] 목표 커버리지 [0~1]
 * @returns {{ strategies, pareto_front, recommended_strategy, budget_utilization_pct }}
 */
function safetyCoverageCostOptimizer({
  fmea_items      = [],
  budget_M        = 3000,
  coverage_target = 0.97,
} = {}) {
  // 안전 대응책 카탈로그 (실무 전형적 범주)
  const COUNTERMEASURES = [
    { id:'CM-01', name:'HW 이중화 (1oo2D)',          cost_M:620, dc_gain:0.12, asil_req:'D', type:'hw' },
    { id:'CM-02', name:'SW 모니터링 (AUTOSAR SafeWD)',cost_M:180, dc_gain:0.07, asil_req:'B', type:'sw' },
    { id:'CM-03', name:'FMEDA 전수 시험',             cost_M:240, dc_gain:0.05, asil_req:'C', type:'test' },
    { id:'CM-04', name:'Plausibility Check (실시간)', cost_M:90,  dc_gain:0.06, asil_req:'B', type:'sw' },
    { id:'CM-05', name:'CRC/ECC 메모리 보호',         cost_M:60,  dc_gain:0.04, asil_req:'A', type:'hw' },
    { id:'CM-06', name:'End-to-End 보호 (E2E Profile 6)',cost_M:75, dc_gain:0.05, asil_req:'B', type:'sw' },
    { id:'CM-07', name:'독립 전압 감시기 (HW)',        cost_M:110, dc_gain:0.06, asil_req:'B', type:'hw' },
    { id:'CM-08', name:'온도 보호 리던던시',            cost_M:80,  dc_gain:0.04, asil_req:'A', type:'hw' },
    { id:'CM-09', name:'Fault Injection 테스트베드',   cost_M:300, dc_gain:0.08, asil_req:'C', type:'test' },
    { id:'CM-10', name:'ISO 26262 Tool C (자동화)',   cost_M:150, dc_gain:0.03, asil_req:'B', type:'tool' },
    { id:'CM-11', name:'SOTIF ODD 커버리지 확장',      cost_M:200, dc_gain:0.05, asil_req:'C', type:'test' },
    { id:'CM-12', name:'Cybersec. 침투 테스트 (TARA)', cost_M:130, dc_gain:0.04, asil_req:'B', type:'cyber' },
  ];

  // FMEA 결과에서 미대응 항목 집계 (coverage gap 도출)
  const unmitigated_count = fmea_items.filter(f =>
    (f.asil === 'D' || f.asil === 'C') && (!f.actions || f.actions.length === 0)
  ).length;
  const base_coverage = fmea_items.length > 0
    ? fmea_items.filter(f => f.asil && f.actions?.length > 0).length / fmea_items.length
    : 0.72;   // FMEA 없을 때 기본 기준선 72%

  // 배낭 문제(Knapsack) 근사: ROI(dc_gain/cost) 정렬 후 그리디
  const sorted_cm = [...COUNTERMEASURES].sort((a,b) =>
    (b.dc_gain / b.cost_M) - (a.dc_gain / a.cost_M)
  );

  // 전략 포트폴리오 생성 (예산 10~100% 5단계)
  const strategies = [0.2, 0.4, 0.6, 0.8, 1.0].map(budget_ratio => {
    const avail = budget_M * budget_ratio;
    let spent = 0, dc = base_coverage;
    const selected = [];

    for (const cm of sorted_cm) {
      if (spent + cm.cost_M > avail) continue;
      spent  += cm.cost_M;
      dc      = Math.min(0.999, dc + cm.dc_gain * (1 - dc)); // diminishing returns
      selected.push(cm.id);
    }

    // 비용 효율 지수: dc gain / budget 단위
    const efficiency = selected.length > 0 ? +((dc - base_coverage) / (spent || 1) * 1000).toFixed(3) : 0;
    const roi_pct    = spent > 0 ? +(((dc - base_coverage) / (spent / budget_M)) * 100).toFixed(1) : 0;

    return {
      budget_ratio,
      budget_M:    +avail.toFixed(0),
      spent_M:     +spent.toFixed(0),
      coverage_pct:+(dc * 100).toFixed(1),
      dc_achieved: +dc.toFixed(4),
      selected_cm: selected,
      cm_count:    selected.length,
      efficiency_idx: efficiency,
      roi_pct,
      meets_target: dc >= coverage_target,
      is_pareto:    false,
    };
  });

  // 파레토 프론티어
  const pareto_front = strategies.filter(a =>
    !strategies.some(b =>
      b.spent_M <= a.spent_M && b.coverage_pct >= a.coverage_pct &&
      (b.spent_M < a.spent_M || b.coverage_pct > a.coverage_pct)
    )
  );
  pareto_front.forEach(p => { const s = strategies.find(x=>x.budget_ratio===p.budget_ratio); if(s) s.is_pareto=true; });

  // 권장: 목표 커버리지 최초 달성 전략
  const recommended_strategy = strategies.find(s => s.meets_target)
    ?? strategies[strategies.length - 1];

  return {
    base_coverage_pct:     +(base_coverage * 100).toFixed(1),
    coverage_target_pct:   +(coverage_target * 100).toFixed(1),
    budget_M,
    unmitigated_count,
    countermeasures:       COUNTERMEASURES,
    strategies,
    pareto_front,
    recommended_strategy,
    budget_utilization_pct:+(recommended_strategy.spent_M / budget_M * 100).toFixed(1),
    iso_ref:               'ISO 26262-4 §8 + ISO 26262-5 FMEDA',
    analysis_note:         `기준선 커버리지 ${+(base_coverage*100).toFixed(1)}% → ` +
      `목표 ${+(coverage_target*100).toFixed(1)}% 달성을 위한 최적 대응책 조합: ` +
      `${recommended_strategy.selected_cm.join(', ')} ` +
      `(예산 ${recommended_strategy.spent_M}M / ${budget_M}M)`,
  };
}

export {
  ASIL_TABLE,
  ASIL_SCORE,
  ASIL_COLOR,

  FTA_TOP_EVENTS,
  FTA_INTERMEDIATE_EVENTS,
  calcASIL,
  calcASIL_safe,                  // [Fix-ASIL-1] null-safe 래퍼
  ASIL_VALID_S, ASIL_VALID_E, ASIL_VALID_C,  // [Fix-ASIL-1] 유효 범위 집합
  INDEPENDENCE_CRITERIA_LIB,      // [Fix-DECOMP-1] 독립성 기준 라이브러리
  DECOMP_INDEPENDENCE_CRITERIA,   // [Fix-DECOMP-1] ASIL별 요구 기준 유형
  computeFMEA,
  runFaultInjectionSeries,
  runFaultSimulation,
  computeSOTIF,
  computeSME,
  computeDFMEA,
  computePFMEA,
  linkDFMEA_PFMEA,
  autoMapDFMEA_PFMEA,
  generateControlPlan,
  buildFaultTree,
  computeFTA,
  analyzeHARA,
  generateFSR_TSR,
  generateHARAReport,
  computeASILDecomposition,
  computeCALRating,
  computePMHFBudget,
  buildTraceabilityMatrix,
  exportAIAGVDA_FMEA,
  computeFTAWithOverrides,
  computeHWSafety,
  computeConfirmationMeasures,   // [RevE-Fix-CM] ISO 26262-2 §6.4 Confirmation Measure
  asilDecompositionOptimizer,   // [RevE] ASIL 분해 최적화기 — 비용·커버리지 파레토
  safetyCoverageCostOptimizer,  // [RevE] 안전 커버리지 vs 비용 최적화기
};